import React, { useCallback, useEffect, useRef, useState } from 'react';
import { recipeUnitCost } from '../../utils/auxiliaryRecipe';
import { createPortal } from 'react-dom';
import * as XLSX from 'xlsx';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import api from '../../api/api';
import {
  fetchDishes, createDish, updateDish, deleteDish, bulkDeleteDishes,
  fetchDishCategories, uploadDishPhoto, recalculateDishAndSemiCosts, undoLastDishAndSemiCostRecalculation,
} from '../../store/slices/dishSlice';
import { createProduct, fetchProductCategories, fetchProducts } from '../../store/slices/productSlice';
import { createSemiFinished, fetchSemiFinished, fetchSemiFinishedCategories } from '../../store/slices/semiFinishedSlice';
import { fetchPreparationOperations } from '../../store/slices/preparationOperationSlice';
import { Dish, NutritionCalculationResult, ProcessingMethod, RecipePreparationStep } from '../../types';
import { getAllergenBadges, getAllergenIcon } from '../../constants/allergenIcons';
import { resolveApiBase, resolveApiUrl } from '../../api/api';
import { getSelectableProducts } from '../../utils/selectableProducts';
import { parseRecipeImportNumber, parseRecipePreparationSteps, uniqueRecipeMatch } from '../../utils/catalogRecipeImport';
import B52Modal from '../../components/B52Modal';
import B52Autocomplete from '../../components/B52Autocomplete';
import SendPdfToTelegramButton from '../../components/SendPdfToTelegramButton';
import ItemQuantityField from '../../components/ItemQuantityField';
import RecipeCalculationCard from '../../components/RecipeCalculationCard';
import RecipePreparationStepsEditor from '../../components/RecipePreparationStepsEditor';
import { renderAndPrintInWindow } from '../../utils/printDocument';
import AuthorizedApiImage from '../../components/AuthorizedApiImage';
import { getArchiveRebuildUnavailableNotice, getHistoricalWriteOffUnavailableNotice } from '../../utils/operatingContext';
import { useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import './dish-recipe-mobile.css';

const WEIGHT_TYPES = [
  { value: 'kg', label: 'кг' }, { value: 'g', label: 'г' },
  { value: 'l', label: 'л' }, { value: 'ml', label: 'мл' }, { value: 'pcs', label: 'шт' },
];
const PAGE_SIZE = 15;
type DishSort = 'name-asc' | 'name-desc' | 'price-asc' | 'price-desc' | 'category-asc' | 'category-desc' | 'cost-asc' | 'cost-desc';
type ArchiveWriteOffRebuildBatch = {
  rebuiltCount: number;
  remainingCount: number;
  nextAfterOrderId: number | null;
  errors: Array<{ eventOrderId: number; message: string }>;
};

// ── Portal autocomplete ───────────────────────────────────────
interface SItem { id: string; name: string; }
const SearchSelect: React.FC<{ items: SItem[]; value: string; onChange: (id: string) => void; placeholder?: string }> = ({ items, value, onChange, placeholder }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const selected = items.find(i => i.id === value);

  const calcPos = () => { if (wrapRef.current) { const r = wrapRef.current.getBoundingClientRect(); setPos({ top: r.bottom + 2, left: r.left, width: r.width }); } };
  const handleOpen = () => { calcPos(); setOpen(true); setTimeout(() => inputRef.current?.focus(), 10); };
  const handleClose = () => { setOpen(false); setQuery(''); };

  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      if (wrapRef.current && !wrapRef.current.contains(t)) {
        const p = document.getElementById('dish-portal');
        if (p && !p.contains(t)) handleClose();
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const filtered = query.trim() ? items.filter(i => i.name.toLowerCase().includes(query.toLowerCase())) : items;
  const select = (i: SItem) => { onChange(i.id); handleClose(); };
  const s: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: '6px', padding: '5px 8px', width: '100%', boxSizing: 'border-box', fontSize: '13px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      {selected && !open
        ? <div style={{ ...s, display: 'flex', alignItems: 'center', gap: '4px', cursor: 'text', minHeight: '30px' }} onClick={handleOpen}>
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.name}</span>
            <button type="button" onClick={e => { e.stopPropagation(); onChange(''); setQuery(''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--crm-text-muted)', fontSize: '16px', padding: '0 2px', lineHeight: 1 }}>×</button>
          </div>
        : <input ref={inputRef} style={s} value={query} placeholder={placeholder ?? '🔍 Пошук...'}
            onChange={e => { setQuery(e.target.value); if (!open) { calcPos(); setOpen(true); } }}
            onFocus={() => { if (!open) { calcPos(); setOpen(true); } }} />}
      {open && createPortal(
        <div id="dish-portal" style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 99999, background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', boxShadow: 'var(--crm-shadow-sm)', maxHeight: '220px', overflowY: 'auto', color: 'var(--crm-text)' }}>
          {filtered.length === 0
            ? <div style={{ padding: '10px 12px', color: 'var(--crm-text-muted)', fontSize: '13px' }}>Нічого не знайдено</div>
            : filtered.map(i => (
                <div key={i.id} onMouseDown={() => select(i)}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '13px', borderBottom: '1px solid var(--crm-table-row-border)', color: 'var(--crm-text)' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--crm-surface-alt)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                  {i.name}
                </div>
              ))}
        </div>, document.body)}
    </div>
  );
};

interface IngredientRow { _key: number; type: 'product' | 'semi'; refId: string; bruttoWeight: string; nettoWeight: string; piecesPerPortion: string; output: string; notes: string; operationNotes: string; preparationOperationId: string; processingMethodId: string; processingTemperatureC: string; }
const hiddenIngredientColumns = {
  notes: false,
  price: false,
  cost: false,
  processing: false,
};
const emptyRow = (k: number): IngredientRow => ({ _key: k, type: 'product', refId: '', bruttoWeight: '', nettoWeight: '', piecesPerPortion: '', output: '', notes: '', operationNotes: '', preparationOperationId: '', processingMethodId: '', processingTemperatureC: '' });
type SavedPrintSelection = {
  id: string;
  name: string;
  selectedIds: number[];
  categoryIds: number[];
};

type DishPrintColumnKey = 'name' | 'brutto' | 'netto' | 'notes' | 'output' | 'unitPrice' | 'rowCost';
type DishPrintColumnSettings = Record<DishPrintColumnKey, boolean>;

const SAVED_PRINT_SELECTIONS_KEY = 'crm4chef.dishes.saved-print-selections.v1';
const DISH_PRINT_COLUMNS_STORAGE_KEY = 'crm4chef.dishes.print-columns.v1';
const defaultDishPrintColumns: DishPrintColumnSettings = {
  name: true,
  brutto: true,
  netto: true,
  notes: true,
  output: false,
  unitPrice: false,
  rowCost: false,
};
const defaultAccountingPrintColumns: DishPrintColumnSettings = {
  name: true,
  brutto: true,
  netto: false,
  notes: false,
  output: false,
  unitPrice: true,
  rowCost: true,
};

interface FormState {
  name: string; b52Code: string; b52Name: string; categoryId: string; description: string;
  cookingTime: string; serveTime: string; weightType: string;
  technologyDescription: string; photoUrl: string; photoBlob?: string;
  preparationSteps: RecipePreparationStep[];
  netWeight: string; grossWeight: string; cleanOutput: string; servingsCount: string;
  marginType: 'percent' | 'amount'; margin: string;
}
const emptyForm: FormState = {
  name: '', b52Code: '', b52Name: '', categoryId: '', description: '', cookingTime: '', serveTime: '', weightType: 'kg',
  technologyDescription: '', photoUrl: '', photoBlob: undefined,
  preparationSteps: [],
  netWeight: '', grossWeight: '', cleanOutput: '', servingsCount: '',
  marginType: 'percent', margin: '0',
};

const DishesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const operatingGroup = useSelectedOperatingGroup();
  const { items, categories, loading } = useAppSelector((s) => s.dish);
  const products = useAppSelector((s) => s.product.products);
  const productCategories = useAppSelector((s) => s.product.categories);
  const semiFinished = useAppSelector((s) => s.semiFinished.items);
  const semiFinishedCategories = useAppSelector((s) => s.semiFinished.categories);
  const preparationOperations = useAppSelector((s) => s.preparationOperation.items);
  const [processingMethods, setProcessingMethods] = useState<ProcessingMethod[]>([]);
  const [nutritionResult, setNutritionResult] = useState<NutritionCalculationResult | null>(null);
  const [nutritionLoading, setNutritionLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [b52ModalOpen, setB52ModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>({ ...emptyForm });
  const [rows, setRows] = useState<IngredientRow[]>([]);
  const [rowKey, setRowKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveArchiveSnapshot, setSaveArchiveSnapshot] = useState(false);
  const [archiveEffectiveFromUtc, setArchiveEffectiveFromUtc] = useState<string>(new Date().toISOString().slice(0, 10));
  const [archiveVersions, setArchiveVersions] = useState<Array<{ id: number; versionNumber: number; effectiveFromUtc: string; effectiveToUtc?: string | null; createdAtUtc: string; createdBy?: string | null; isManualArchive: boolean; snapshotJson?: string | null }>>([]);
  const [selectedArchiveVersionId, setSelectedArchiveVersionId] = useState<number | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [recipeWorkbookFile, setRecipeWorkbookFile] = useState<File | null>(null);
  const [recipeWorkbookPreview, setRecipeWorkbookPreview] = useState<{
    canApply: boolean; imported: number; updated: number; errors: string[];
  } | null>(null);
  const [recalculatingCosts, setRecalculatingCosts] = useState(false);
  const [undoingRecalculateCosts, setUndoingRecalculateCosts] = useState(false);
  const [scaleBasis, setScaleBasis] = useState<'brutto' | 'netto' | 'output'>('brutto');
  const [scaleTargetWeight, setScaleTargetWeight] = useState('');
  const [showQuickProduct, setShowQuickProduct] = useState(false);
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [quickProduct, setQuickProduct] = useState({ name: '', price: '', weightType: 'kg', categoryId: '' });
  const [showSemiFromRows, setShowSemiFromRows] = useState(false);
  const [creatingSemi, setCreatingSemi] = useState(false);
  const [semiFromRows, setSemiFromRows] = useState<{ name: string; categoryId: string; output: string; replaceRows: boolean; selectedKeys: number[] }>({ name: '', categoryId: '', output: '', replaceRows: true, selectedKeys: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<DishSort>('name-asc');
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [finalPriceInput, setFinalPriceInput] = useState('');
  const [isEditingFinalPrice, setIsEditingFinalPrice] = useState(false);
  const [lockedFinalPrice, setLockedFinalPrice] = useState<number | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [detailsDish, setDetailsDish] = useState<Dish | null>(null);
  const [detailsPortions, setDetailsPortions] = useState('1');
  const printPortions = '1';
  const [detailsWorkStart, setDetailsWorkStart] = useState('');
  const [detailsWorkEnd, setDetailsWorkEnd] = useState('');
  const [detailsWorkPortions, setDetailsWorkPortions] = useState('');
  const [printManageModalOpen, setPrintManageModalOpen] = useState(false);
  const [savedPrintSelections, setSavedPrintSelections] = useState<SavedPrintSelection[]>([]);
  const [printSelectionName, setPrintSelectionName] = useState('');
  const [printCategoryIds, setPrintCategoryIds] = useState<number[]>([]);
  const [dishPrintColumns, setDishPrintColumns] = useState<DishPrintColumnSettings>(defaultDishPrintColumns);
  const [accountingPrintColumns, setAccountingPrintColumns] = useState<DishPrintColumnSettings>(defaultAccountingPrintColumns);
  const [ingredientColumns, setIngredientColumns] = useState(hiddenIngredientColumns);
  const [replaceIngredientsModalOpen, setReplaceIngredientsModalOpen] = useState(false);
  const [replaceFromIngredientId, setReplaceFromIngredientId] = useState('');
  const [replaceToIngredientId, setReplaceToIngredientId] = useState('');
  const [replacingIngredients, setReplacingIngredients] = useState(false);
  const [deleteByCategoryModalOpen, setDeleteByCategoryModalOpen] = useState(false);
  const [deleteCategoryIds, setDeleteCategoryIds] = useState<number[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const pendingPhotoRef = useRef<File | null>(null);
  const archiveRebuildAbort = useRef<AbortController | null>(null);
  const archiveRebuildIsCurrent = useRef<(() => boolean) | null>(null);
  const importRef = useRef<HTMLInputElement>(null);
  const recipeWorkbookRef = useRef<HTMLInputElement>(null);
  const isOperatingGroupCurrent = operatingGroup.isCurrent;

  useEffect(() => {
    if (archiveRebuildAbort.current && archiveRebuildIsCurrent.current && !archiveRebuildIsCurrent.current())
      archiveRebuildAbort.current.abort();
  }, [operatingGroup.groupId, operatingGroup.changedInAnotherTab, isOperatingGroupCurrent]);
  useEffect(() => () => archiveRebuildAbort.current?.abort(), []);

  useEffect(() => {
    dispatch(fetchDishes());
    dispatch(fetchDishCategories());
    dispatch(fetchProducts());
    dispatch(fetchProductCategories());
    dispatch(fetchSemiFinished());
    dispatch(fetchPreparationOperations());
    api.get<ProcessingMethod[]>('/processing-methods')
      .then((res) => setProcessingMethods(res.data))
      .catch(() => setProcessingMethods([]));
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(SAVED_PRINT_SELECTIONS_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as SavedPrintSelection[];
      if (Array.isArray(parsed)) setSavedPrintSelections(parsed);
    } catch {
      // ignore invalid local storage payload
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(SAVED_PRINT_SELECTIONS_KEY, JSON.stringify(savedPrintSelections));
    } catch {
      // ignore storage errors
    }
  }, [savedPrintSelections]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISH_PRINT_COLUMNS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        dish?: Partial<DishPrintColumnSettings>;
        accounting?: Partial<DishPrintColumnSettings>;
      };
      if (parsed.dish) {
        setDishPrintColumns((prev) => ({ ...prev, ...parsed.dish }));
      }
      if (parsed.accounting) {
        setAccountingPrintColumns((prev) => ({ ...prev, ...parsed.accounting }));
      }
    } catch {
      // ignore invalid local storage payload
    }
  }, []);

  useEffect(() => {
    if (modalOpen) setIngredientColumns(hiddenIngredientColumns);
  }, [modalOpen]);

  useEffect(() => {
    try {
      localStorage.setItem(
        DISH_PRINT_COLUMNS_STORAGE_KEY,
        JSON.stringify({ dish: dishPrintColumns, accounting: accountingPrintColumns })
      );
    } catch {
      // ignore storage errors
    }
  }, [dishPrintColumns, accountingPrintColumns]);

  const extractError = (err: any, fallback: string) =>
    err?.response?.data?.message
    || (Array.isArray(err?.response?.data?.errors) ? err.response.data.errors.join(' ') : null)
    || err?.message
    || fallback;

  const formatDurationInput = (seconds: number | null | undefined) => {
    const normalized = Number(seconds ?? 0);
    if (!Number.isFinite(normalized) || normalized <= 0) return '';
    const safe = Math.max(0, Math.floor(normalized));
    const minutes = Math.floor(safe / 60);
    const restSeconds = safe % 60;
    return `${minutes},${restSeconds}`;
  };

  const parseDurationToSeconds = (rawValue: string, fallback = 0) => {
    const value = rawValue.trim();
    if (!value) return fallback;
    const normalized = value.replace(/\s+/g, '').replace('.', ',');
    if (normalized.includes(',') || normalized.includes(':')) {
      const [minutesPart, secondsPart = '0'] = normalized.split(/[,:]/);
      const minutes = parseInt(minutesPart, 10);
      const normalizedSecondsPart = secondsPart.length === 1 ? `${secondsPart}0` : secondsPart;
      const seconds = parseInt(normalizedSecondsPart, 10);
      if (Number.isNaN(minutes) || Number.isNaN(seconds)) return fallback;
      if (minutes < 0 || seconds < 0) return fallback;
      const minuteCarry = Math.floor(seconds / 60);
      const normalizedSeconds = seconds % 60;
      return minutes * 60 + minuteCarry * 60 + normalizedSeconds;
    }
    const totalMinutes = parseInt(normalized, 10);
    return Number.isNaN(totalMinutes) ? fallback : Math.max(0, totalMinutes) * 60;
  };

  const formatDurationLabel = (seconds: number | null | undefined) => {
    const normalized = Number(seconds ?? 0);
    if (!Number.isFinite(normalized) || normalized <= 0) return '—';
    const safe = Math.max(0, Math.floor(normalized));
    const hours = Math.floor(safe / 3600);
    const minutes = Math.floor((safe % 3600) / 60);
    const restSeconds = safe % 60;
    if (hours > 0) return `${hours} год ${minutes} хв ${restSeconds} сек`;
    return `${minutes} хв ${restSeconds} сек`;
  };
  const calcRequiredTime = (secondsPerPortion: number | null | undefined, portions: number) => {
    const safeSeconds = Number.isFinite(Number(secondsPerPortion)) ? Math.max(0, Number(secondsPerPortion)) : 0;
    const safePortions = Number.isFinite(portions) ? Math.max(0, portions) : 0;
    return safeSeconds * safePortions;
  };
  const parseClockToSeconds = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parts = trimmed.split(':');
    if (parts.length !== 2) return null;
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return null;
    if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null;
    return hours * 3600 + minutes * 60;
  };

  const selectableIngredientProducts = React.useMemo(
    () => getSelectableProducts(
      products,
      rows.filter((row) => row.type === 'product').map((row) => row.refId)
    ),
    [products, rows]
  );

  // Combined ingredient search: products + semi-finished
  const allIngredients = React.useMemo(() => [
    ...selectableIngredientProducts.map(p => ({ id: `p:${p.id}`, name: p.name })),
    ...semiFinished.map(sf => ({ id: `s:${sf.id}`, name: `${sf.name} [Н/Ф]` })),
  ], [selectableIngredientProducts, semiFinished]);

  const decodeIngredientId = (encoded: string) => {
    if (!encoded) return { type: 'product' as const, refId: '' };
    if (encoded.startsWith('p:')) return { type: 'product' as const, refId: encoded.slice(2) };
    return { type: 'semi' as const, refId: encoded.slice(2) };
  };

  const encodeIngredientId = (row: IngredientRow) =>
    row.refId ? `${row.type === 'product' ? 'p' : 's'}:${row.refId}` : '';

  const nextKey = () => { const k = rowKey + 1; setRowKey(k); return k; };
  const formatArchiveVersionDate = (value?: string | null) => {
    if (!value) return '—';
    const datePart = String(value).split('T')[0];
    if (!datePart || datePart === '0001-01-01') return '—';
    const parsed = new Date(`${datePart}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? datePart : parsed.toLocaleDateString('uk-UA');
  };

  const restoreArchiveVersion = (version: { id: number; snapshotJson?: string | null }) => {
    if (!version.snapshotJson) {
      setError('Архівна версія не містить даних для відновлення.');
      return;
    }

    try {
      const parsed = JSON.parse(version.snapshotJson) as { name?: string; Name?: string; ingredients?: Array<any>; Ingredients?: Array<any> };
      const archivedIngredients = parsed.ingredients ?? parsed.Ingredients ?? [];
      const restoredRows = archivedIngredients.map((ingredient, index) => {
        const productId = ingredient.productId ?? ingredient.ProductId ?? null;
        const semiId = ingredient.semiFinishedProductId ?? ingredient.SemiFinishedProductId ?? null;
        return {
          _key: index + 1,
          type: productId != null ? 'product' : 'semi',
          refId: String(productId ?? semiId ?? ''),
          bruttoWeight: String(Number(ingredient.bruttoWeight ?? ingredient.BruttoWeight ?? ingredient.quantity ?? ingredient.Quantity ?? 0) || 0),
          nettoWeight: String(Number(ingredient.nettoWeight ?? ingredient.NettoWeight ?? 0) || 0),
          piecesPerPortion: String(ingredient.piecesPerPortion ?? ingredient.PiecesPerPortion ?? ''),
          output: String(Number(ingredient.output ?? ingredient.Output ?? 0) || 0),
          notes: ingredient.notes ?? ingredient.Notes ?? '',
          operationNotes: ingredient.operationNotes ?? ingredient.OperationNotes ?? '',
          preparationOperationId: (ingredient.preparationOperationId ?? ingredient.PreparationOperationId) != null ? String(ingredient.preparationOperationId ?? ingredient.PreparationOperationId) : '',
          processingMethodId: (ingredient.processingMethodId ?? ingredient.ProcessingMethodId) != null ? String(ingredient.processingMethodId ?? ingredient.ProcessingMethodId) : '',
          processingTemperatureC: (ingredient.processingTemperatureC ?? ingredient.ProcessingTemperatureC) != null ? String(ingredient.processingTemperatureC ?? ingredient.ProcessingTemperatureC) : '',
        } satisfies IngredientRow;
      });

      setForm((prev) => ({ ...prev, name: parsed.name || parsed.Name || prev.name }));
      setRows(restoredRows.length > 0 ? restoredRows : [emptyRow(0)]);
      setRowKey(restoredRows.length > 0 ? restoredRows.length : 1);
      setSelectedArchiveVersionId(version.id);
      setSaveArchiveSnapshot(false);
      setError('Архівну калькуляцію завантажено в форму для редагування. Збережіть її як поточну версію.');
    } catch {
      setError('Не вдалося розпакувати архівну калькуляцію.');
    }
  };

  const loadArchiveVersions = async (entityType: string, entityId: number) => {
    if (!entityId) {
      setArchiveVersions([]);
      setSelectedArchiveVersionId(null);
      return;
    }

    try {
      const { data } = await api.get<typeof archiveVersions>(`/RecipeVersionSnapshots/${encodeURIComponent(entityType)}/${entityId}`);
      const normalized = Array.isArray(data) ? data : [];
      setArchiveVersions(normalized);
      setSelectedArchiveVersionId((prev) =>
        prev != null && normalized.some((version) => version.id === prev)
          ? prev
          : (normalized[0]?.id ?? null));
    } catch {
      setArchiveVersions([]);
      setSelectedArchiveVersionId(null);
      setError('Не вдалося завантажити архівні версії калькуляції.');
    }
  };

  const deleteArchiveVersion = async (version: { id: number; versionNumber: number }) => {
    if (!editingId || !window.confirm(`Видалити архівну калькуляцію v${version.versionNumber}? Цю дію неможливо скасувати.`)) return;
    try {
      setError(null);
      await api.delete(`/RecipeVersionSnapshots/${version.id}`);
      await loadArchiveVersions('Dish', editingId);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Не вдалося видалити архівну калькуляцію.');
    }
  };

  const openNew = () => {    setEditingId(null); setSaveArchiveSnapshot(false); setArchiveEffectiveFromUtc(new Date().toISOString().slice(0, 10)); setArchiveVersions([]); setSelectedArchiveVersionId(null); setForm({ ...emptyForm }); setRows([emptyRow(0)]); setRowKey(1);
    setLockedFinalPrice(null); setFinalPriceInput(''); setIsEditingFinalPrice(false);
    setScaleBasis('brutto'); setScaleTargetWeight('');
    setError(null); pendingPhotoRef.current = null; setModalOpen(true);
    setShowQuickProduct(false); setShowSemiFromRows(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
  };

  const openEdit = (d: Dish) => {
    setEditingId(d.id); setSaveArchiveSnapshot(false); setArchiveEffectiveFromUtc(new Date().toISOString().slice(0, 10)); loadArchiveVersions('Dish', d.id);
    const normalizedMargin = d.marginType === 'amount'
      ? (Number(d.costPrice) > 0 ? Math.max(0, (Number(d.margin ?? 0) / Number(d.costPrice)) * 100) : 0)
      : Number(d.margin ?? 0);
    setForm({
      name: d.name, b52Code: d.b52Code ?? '', b52Name: d.b52Name ?? '', categoryId: d.categoryId != null ? String(d.categoryId) : '',
      description: d.description, cookingTime: formatDurationInput(d.cookingTime), serveTime: formatDurationInput(d.serveTime), weightType: 'kg',
      technologyDescription: d.technologyDescription, photoUrl: d.photoUrl, photoBlob: undefined,
      preparationSteps: d.preparationSteps ?? [],
      netWeight: String(d.netWeight), grossWeight: String(d.grossWeight), cleanOutput: String(d.cleanOutput), servingsCount: d.servingsCount != null ? String(d.servingsCount) : '',
      marginType: 'percent', margin: String(Math.max(0, Math.round(normalizedMargin))),
    });
    let k = 0;
    setRows(d.ingredients.map(i => ({
      _key: k++, type: i.productId != null ? 'product' : 'semi',
      refId: String(i.productId ?? i.semiFinishedProductId ?? ''),
      bruttoWeight: String(i.bruttoWeight), nettoWeight: String(i.nettoWeight), piecesPerPortion: String(i.piecesPerPortion ?? ''), output: String(i.output), notes: i.notes ?? '',
      operationNotes: i.operationNotes ?? '',
      preparationOperationId: i.preparationOperationId != null ? String(i.preparationOperationId) : '',
      processingMethodId: i.processingMethodId != null ? String(i.processingMethodId) : '',
      processingTemperatureC: i.processingTemperatureC != null ? String(i.processingTemperatureC) : '',
    })));
    setScaleBasis('brutto'); setScaleTargetWeight('');
    setRowKey(k); setLockedFinalPrice(Math.round(Number(d.finalPrice))); setFinalPriceInput(''); setIsEditingFinalPrice(false); setError(null); pendingPhotoRef.current = null; setModalOpen(true);
    setShowQuickProduct(false); setShowSemiFromRows(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
    setNutritionResult(null);
  };

  useEffect(() => {
    if (!modalOpen || editingId == null) return;
    const current = items.find((dish) => dish.id === editingId);
    if (!current) return;
    setForm((prev) => {
      const nextServingsCount = current.servingsCount != null ? String(current.servingsCount) : '';
      if (prev.servingsCount === nextServingsCount) return prev;
      return { ...prev, servingsCount: nextServingsCount };
    });
  }, [modalOpen, editingId, items]);

  const closeModal = () => {
    setModalOpen(false); setError(null);
    setLockedFinalPrice(null); setFinalPriceInput(''); setIsEditingFinalPrice(false);
    setScaleBasis('brutto'); setScaleTargetWeight('');
    if (form.photoBlob) URL.revokeObjectURL(form.photoBlob);
    setShowQuickProduct(false); setShowSemiFromRows(false);
    setCreatingProduct(false);
    setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
  };
  const openDetailsModal = (dish: Dish) => {
    setDetailsDish(dish);
    setDetailsPortions('1');
    setDetailsWorkStart('');
    setDetailsWorkEnd('');
    setDetailsWorkPortions('');
    setDetailsModalOpen(true);
  };
  const getDishComputedAllergens = (dish: Dish) => {
    const fromIngredients = dish.ingredients.flatMap((ingredient) => {
      if (ingredient.productId != null) {
        return products.find((product) => product.id === ingredient.productId)?.allergens ?? [];
      }
      if (ingredient.semiFinishedProductId != null) {
        return semiFinished.find((item) => item.id === ingredient.semiFinishedProductId)?.allergens ?? [];
      }
      return [];
    });
    const merged = [...fromIngredients, ...(dish.allergens ?? [])];
    return merged.filter((allergen, index, arr) => arr.findIndex((entry) => entry.id === allergen.id) === index);
  };
  const loadNutrition = async () => {
    if (!editingId) {
      setError('Розрахунок харчової цінності доступний після збереження страви.');
      return;
    }
    setNutritionLoading(true);
    setError(null);
    try {
      const res = await api.get<NutritionCalculationResult>(`/dishes/${editingId}/nutrition`);
      setNutritionResult(res.data);
    } catch (err: any) {
      setNutritionResult(null);
      setError(err?.response?.data?.message || 'Не вдалося розрахувати харчову цінність.');
    } finally {
      setNutritionLoading(false);
    }
  };
  const addRow = () => setRows(r => [...r, emptyRow(nextKey())]);
  const removeRow = (key: number) => setRows(r => r.filter(x => x._key !== key));
  const updateRow = (key: number, patch: Partial<IngredientRow>) =>
    setRows(r => r.map(x => x._key === key ? { ...x, ...patch } : x));

  const handleQuickCreateProduct = async () => {
    const name = quickProduct.name.trim();
    const price = parseFloat(quickProduct.price);
    if (!name) { setError("Вкажіть назву нового продукту"); return; }
    if (Number.isNaN(price) || price < 0) { setError('Вкажіть коректну ціну продукту'); return; }

    setCreatingProduct(true);
    setError(null);
    try {
      const created = await dispatch(createProduct({
        name,
        price,
        weightType: quickProduct.weightType,
        categoryId: quickProduct.categoryId ? parseInt(quickProduct.categoryId, 10) : null,
        supplierId: null,
        description: '',
        isActive: true,
        allergenIds: [],
      })).unwrap();

      await dispatch(fetchProducts());
      setRows((prev) => {
        const emptyProductRowIndex = prev.findIndex((r) => !r.refId);
        if (emptyProductRowIndex === -1) return prev;
        const next = [...prev];
        next[emptyProductRowIndex] = { ...next[emptyProductRowIndex], type: 'product', refId: String(created.id) };
        return next;
      });
      setQuickProduct({ name: '', price: '', weightType: 'kg', categoryId: '' });
      setShowQuickProduct(false);
    } catch {
      setError('Не вдалося створити продукт');
    } finally {
      setCreatingProduct(false);
    }
  };

  // Builds a semi-finished product from selected composition rows and optionally swaps them for it,
  // keeping the dish cost unchanged (Н/Ф unit price = cost / output).
  const openSemiFromRows = () => {
    if (!showSemiFromRows) {
      const keys = rows.filter((r) => r.refId).map((r) => r._key);
      const output = rows.filter((r) => keys.includes(r._key)).reduce((s, r) => s + (parseFloat(r.output) || 0), 0);
      setSemiFromRows({ name: form.name ? `${form.name} (Н/Ф)` : '', categoryId: '', output: output > 0 ? String(+output.toFixed(3)) : '', replaceRows: true, selectedKeys: keys });
      dispatch(fetchSemiFinishedCategories());
    }
    setShowSemiFromRows((v) => !v);
  };

  const toggleSemiRow = (key: number) => setSemiFromRows((prev) => {
    const selectedKeys = prev.selectedKeys.includes(key) ? prev.selectedKeys.filter((k) => k !== key) : [...prev.selectedKeys, key];
    const output = rows.filter((r) => selectedKeys.includes(r._key)).reduce((s, r) => s + (parseFloat(r.output) || 0), 0);
    return { ...prev, selectedKeys, output: output > 0 ? String(+output.toFixed(3)) : '' };
  });

  const handleCreateSemiFromRows = async () => {
    const name = semiFromRows.name.trim();
    const selected = rows.filter((r) => r.refId && semiFromRows.selectedKeys.includes(r._key));
    const output = parseFloat(semiFromRows.output);
    if (!name) { setError('Вкажіть назву нового Н/Ф'); return; }
    if (selected.length === 0) { setError('Оберіть хоча б один рядок складу для Н/Ф'); return; }
    if (Number.isNaN(output) || output <= 0) { setError('Вкажіть вихід Н/Ф більше 0'); return; }
    if (semiFromRows.replaceRows && selected.some(row => row.piecesPerPortion.trim()) &&
      !window.confirm('Штук на порцію задано для рядків страви. При заміні цих рядків на Н/Ф їхні окремі параметри штук не перенесуться. Продовжити заміну?')) return;
    const toNumber = (v: string) => parseFloat(v) || 0;
    const toNullableInt = (v: string) => (v ? parseInt(v, 10) : null);

    setCreatingSemi(true);
    setError(null);
    try {
      const created = await dispatch(createSemiFinished({
        name,
        b52Code: '',
        b52Name: '',
        categoryId: semiFromRows.categoryId ? parseInt(semiFromRows.categoryId, 10) : null,
        description: '',
        cookingTime: 0,
        serveTime: 0,
        weightType: 'kg',
        netWeight: output,
        grossWeight: selected.reduce((s, r) => s + toNumber(r.bruttoWeight), 0),
        excludeFromDishWeightCalc: false,
        items: selected.map((r) => ({
          productId: r.type === 'product' ? parseInt(r.refId, 10) : null,
          subSemiFinishedProductId: r.type === 'semi' ? parseInt(r.refId, 10) : null,
          quantity: toNumber(r.bruttoWeight),
          bruttoWeight: toNumber(r.bruttoWeight),
          nettoWeight: toNumber(r.nettoWeight),
          output: toNumber(r.output),
          notes: r.notes || null,
          operationNotes: r.operationNotes || null,
          preparationOperationId: toNullableInt(r.preparationOperationId),
          processingMethodId: toNullableInt(r.processingMethodId),
          processingTemperatureC: r.processingTemperatureC ? parseFloat(r.processingTemperatureC) : null,
        })),
      })).unwrap();
      await dispatch(fetchSemiFinished());

      if (semiFromRows.replaceRows) {
        const weight = String(+output.toFixed(3));
        const semiRow: IngredientRow = { ...emptyRow(nextKey()), type: 'semi', refId: String(created.id), bruttoWeight: weight, nettoWeight: weight, output: weight };
        setRows((prev) => {
          const selectedSet = new Set(selected.map((r) => r._key));
          const insertAt = prev.findIndex((r) => selectedSet.has(r._key));
          const rest = prev.filter((r) => !selectedSet.has(r._key));
          const before = prev.slice(0, insertAt).filter((r) => !selectedSet.has(r._key)).length;
          return [...rest.slice(0, before), semiRow, ...rest.slice(before)];
        });
      }
      setShowSemiFromRows(false);
    } catch (e: any) {
      setError(e?.message || 'Не вдалося створити Н/Ф');
    } finally {
      setCreatingSemi(false);
    }
  };

  // Returns effective unit price for a semi-finished product based on its factual output.
  const getSFPrice = (sfId: number): number => {
    const sf = semiFinished.find(i => i.id === sfId);
    if (!sf) return 0;
    const totalCost = sf.costPrice > 0 ? sf.costPrice : sf.items.reduce((sum, item) => {
      if (item.productId != null) {
        const p = products.find(pr => pr.id === item.productId);
        return sum + (p?.price ?? 0) * item.bruttoWeight;
      }
      if (item.subSemiFinishedProductId != null) {
        return sum + getSFPrice(item.subSemiFinishedProductId) * item.bruttoWeight;
      }
      return sum;
    }, 0);
    const factualOutput = Number(sf.netWeight) || 0;
    return recipeUnitCost(totalCost, factualOutput, sf.isAuxiliaryRecipe);
  };

  const calcCost = () => {
    let c = 0;
    for (const row of rows) {
      const b = parseFloat(row.bruttoWeight) || 0;
      if (row.type === 'product') { const p = products.find(x => x.id === parseInt(row.refId)); if (p) c += p.price * b; }
      else {
        const sf = semiFinished.find(s => s.id === parseInt(row.refId));
        if (sf) c += getSFPrice(parseInt(row.refId)) * b;
      }
    }
    return c;
  };

  const cost = calcCost();
  const marginNum = parseFloat(form.margin) || 0;
  const calculatedFinalPrice = cost * (1 + marginNum / 100);
  const finalPrice = lockedFinalPrice ?? calculatedFinalPrice;
  const marginAmount = cost * marginNum / 100;
  const roundToNonNegativePrice = (value: number) => Math.max(0, Math.round(value * 100) / 100);
  const getRoundedFinalPriceForMargin = useCallback(
    (marginValue: number) => roundToNonNegativePrice(cost * (1 + marginValue / 100)),
    [cost]
  );
  const getIntegerMarginForTargetFinalPrice = useCallback((targetFinalPrice: number) => {
    const safeTarget = roundToNonNegativePrice(targetFinalPrice);
    const rough = cost <= 0 ? 0 : Math.max(0, Math.round(((safeTarget / cost) - 1) * 100));

    const candidates = new Set<number>([0, rough]);
    for (let delta = -4; delta <= 4; delta += 1) {
      const candidate = rough + delta;
      if (candidate >= 0) candidates.add(candidate);
    }

    let bestMargin = rough;
    let bestScore = Number.POSITIVE_INFINITY;
    Array.from(candidates).forEach((candidate) => {
      const roundedFinal = getRoundedFinalPriceForMargin(candidate);
      const score = Math.abs(roundedFinal - safeTarget) * 1000 + Math.abs(candidate - rough);
      if (score < bestScore) {
        bestScore = score;
        bestMargin = candidate;
      }
    });
    return bestMargin;
  }, [cost, getRoundedFinalPriceForMargin]);
  const applyMarginFromFinalPrice = (parsedFinalPrice: number) => {
    const safeFinalPrice = roundToNonNegativePrice(parsedFinalPrice);
    setLockedFinalPrice(safeFinalPrice);
    const percent = getIntegerMarginForTargetFinalPrice(safeFinalPrice);
    setForm((prev) => ({ ...prev, marginType: 'percent', margin: String(percent) }));
  };

  useEffect(() => {
    if (!modalOpen || lockedFinalPrice == null || isEditingFinalPrice) return;

    const nextMargin = getIntegerMarginForTargetFinalPrice(lockedFinalPrice);
    if (Math.abs(marginNum - nextMargin) > 0.5) {
      setForm((prev) => ({ ...prev, marginType: 'percent', margin: String(nextMargin) }));
    }
  }, [cost, getIntegerMarginForTargetFinalPrice, isEditingFinalPrice, lockedFinalPrice, marginNum, modalOpen]);
  const isExcludedSFRow = (r: { type: string; refId: string }) =>
    r.type === 'semi' && (() => {
      const semi = semiFinished.find(s => s.id === parseInt(r.refId));
      return semi?.isAuxiliaryRecipe || semi?.excludeFromDishWeightCalc || false;
    })();
  const totalBrutto = rows.reduce((s, r) => s + (isExcludedSFRow(r) ? 0 : (parseFloat(r.bruttoWeight) || 0)), 0);
  const totalNetto  = rows.reduce((s, r) => s + (isExcludedSFRow(r) ? 0 : (parseFloat(r.nettoWeight) || 0)), 0);
  const totalOutput = rows.reduce((s, r) => s + (isExcludedSFRow(r) ? 0 : (parseFloat(r.output) || 0)), 0);
  const totalLossPercent = totalBrutto > 0 ? ((totalBrutto - totalOutput) / totalBrutto) * 100 : 0;
  const cleanOutputNum = parseFloat(form.cleanOutput);
  const readyDishOutput = !Number.isNaN(cleanOutputNum) && cleanOutputNum >= 0 ? cleanOutputNum : totalOutput;
  const toWeightString = (value: number) => {
    const rounded = Math.max(0, Math.round(value * 1000) / 1000);
    return rounded.toFixed(3).replace(/\.?0+$/, '');
  };
  const handleScaleIngredients = () => {
    const target = parseFloat(scaleTargetWeight.replace(',', '.'));
    if (!Number.isFinite(target) || target <= 0) {
      setError('Вкажіть цільову вагу більше 0.');
      return;
    }

    const currentTotal = scaleBasis === 'brutto'
      ? totalBrutto
      : (scaleBasis === 'netto' ? totalNetto : totalOutput);

    if (currentTotal <= 0) {
      setError(`Неможливо перерахувати: поточна сума по "${scaleBasis === 'brutto' ? 'брутто' : scaleBasis === 'netto' ? 'нетто' : 'виходу'}" дорівнює 0.`);
      return;
    }

    const ratio = target / currentTotal;
    setRows((prev) => prev.map((row) => {
      const brutto = parseFloat(row.bruttoWeight) || 0;
      const netto = parseFloat(row.nettoWeight) || 0;
      const output = parseFloat(row.output) || 0;
      return {
        ...row,
        bruttoWeight: toWeightString(brutto * ratio),
        nettoWeight: toWeightString(netto * ratio),
        piecesPerPortion: row.piecesPerPortion.trim() ? toWeightString(Number(row.piecesPerPortion) * ratio) : '',
        output: toWeightString(output * ratio),
      };
    }));
    setError(null);
  };

  const apiBase = resolveApiBase();
  const backendOrigin = apiBase.startsWith('http')
    ? apiBase.replace(/\/api\/?$/, '')
    : window.location.origin;
  const imgSrc = (url: string) => {
    if (!url) return '';
    if (/^(blob:|data:)/i.test(url)) return url;
    if (/^\/dishes\/\d+\/photo$/.test(url)) return resolveApiUrl(url);
    if (/^https?:\/\//i.test(url)) {
      try {
        const absolute = new URL(url);
        if (absolute.pathname.startsWith('/uploads/')) {
          return `${backendOrigin}${absolute.pathname}${absolute.search}${absolute.hash}`;
        }
      } catch {
        // Keep original value when URL parsing fails.
      }
      return url;
    }
    return url.startsWith('/') ? `${backendOrigin}${url}` : `${backendOrigin}/${url}`;
  };

  const getProductAccountingLabel = (productId: number | null, fallbackName: string | null) => {
    const product = productId != null ? products.find((p) => p.id === productId) : null;
    const labelName = product?.b52Name?.trim() || fallbackName || '—';
    const code = product?.b52Code?.trim() || '';
    return code ? `${code} — ${labelName}` : labelName;
  };

  const getSemiAccountingLabel = (semiId: number | null, fallbackName: string | null) => {
    const item = semiId != null ? semiFinished.find((s) => s.id === semiId) : null;
    const labelName = item?.b52Name?.trim() || fallbackName || '—';
    const code = item?.b52Code?.trim() || '';
    return code ? `${code} — ${labelName}` : labelName;
  };

  const openPrintWindow = async (html: string, autoPrint = true) => {
    const printWindow = window.open('', '_blank', 'width=950,height=780');
    if (!printWindow) {
      alert('Дозвольте спливаючі вікна для друку');
      return;
    }
    try {
      const photoIds = Array.from(html.matchAll(/src="[^"]*\/dishes\/(\d+)\/photo"/g))
        .map((match) => Number(match[1]));
      for (const photoId of Array.from(new Set(photoIds))) {
        const photoPath = `/dishes/${photoId}/photo`;
        const photoSource = imgSrc(photoPath);
        const { data } = await api.get<Blob>(photoPath, { responseType: 'blob' });
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => typeof reader.result === 'string'
            ? resolve(reader.result)
            : reject(new Error('Unable to read dish photo.'));
          reader.onerror = () => reject(reader.error ?? new Error('Unable to read dish photo.'));
          reader.readAsDataURL(data);
        });
        html = html.replaceAll(`src="${photoSource}"`, `src="${dataUrl}"`);
      }
      const printed = await renderAndPrintInWindow(printWindow, html, { autoPrint, minimumDelayMs: 500 });
      if (!printed && !printWindow.closed) printWindow.close();
    } catch (error) {
      if (!printWindow.closed) printWindow.close();
      setError(error instanceof Error ? error.message : 'Не вдалося завантажити фото для друку.');
    }
  };

  const printDishHtml = (html: string, _photoUrl: string, autoPrint = true) =>
    openPrintWindow(html, autoPrint);

  const buildPrintHtml = (
    name: string, syncCode: string, catName: string | null, cookTime: number, wtType: string,
    serveTime: number, desc: string, tech: string, photoUrl: string,
    ingRows: Array<{ label: string; notes: string; brutto: number; netto: number; pieces?: number | null; output: number; unitPrice: number; rowCost: number }>,
    tBrutto: number, tNetto: number, tOutput: number,
    costP: number, mType: string, mVal: number, fPrice: number,
    factualOutput: number, allergenBadges: string[], portions = 1,
    columns: DishPrintColumnSettings,
    ingredientColumnTitle = 'Назва інгредієнта'
  ) => {
    const hasPhoto = !!photoUrl;
    const photoPanelContent = hasPhoto
      ? `<img src="${imgSrc(photoUrl)}" style="width:280px;height:280px;object-fit:cover;border-radius:10px;border:1px solid #ddd;" />`
      : `<div class="sheet-photo-placeholder"><div class="icon">🖼</div><div>Тут має бути Фото</div></div>`;
    const photoHtml = `<div class="sheet-photo-panel">${photoPanelContent}</div>`;
    const columnDefs: Array<{
      key: DishPrintColumnKey;
      title: string;
      width?: string;
      align?: 'left' | 'right';
      value: (row: (typeof ingRows)[number]) => string;
      total?: () => string;
    }> = [
      { key: 'name', title: ingredientColumnTitle, align: 'left', value: (row) => `<strong>${row.label}</strong>` },
      { key: 'brutto', title: 'Брутто', width: '78px', align: 'right', value: (row) => row.brutto.toFixed(3), total: () => tBrutto.toFixed(3) },
      { key: 'netto', title: 'Нетто / шт.', width: '98px', align: 'right', value: (row) => `${row.netto.toFixed(3)}${row.pieces != null ? `<br/>${Number(row.pieces.toFixed(4))} шт.` : ''}`, total: () => tNetto.toFixed(3) },
      { key: 'notes', title: 'Нотатки', width: '180px', align: 'left', value: (row) => row.notes ? `<em style="font-style:italic;">${row.notes}</em>` : '—' },
      { key: 'output', title: 'Вихід', width: '78px', align: 'right', value: (row) => row.output.toFixed(3), total: () => tOutput.toFixed(3) },
      { key: 'unitPrice', title: 'Ціна/од', width: '90px', align: 'right', value: (row) => row.unitPrice.toFixed(2) },
      { key: 'rowCost', title: 'Вартість', width: '92px', align: 'right', value: (row) => row.rowCost.toFixed(2), total: () => costP.toFixed(2) },
    ];
    const visibleColumns = columnDefs.filter((column) => columns[column.key]);
    const safeColumns = visibleColumns.length > 0 ? visibleColumns : [columnDefs[0]];
    const rows2 = ingRows.map((r) => `
      <tr style="border-bottom:1px solid #eee;">
        ${safeColumns.map((column) => `<td style="padding:5px 8px;text-align:${column.align ?? 'left'};">${column.value(r)}</td>`).join('')}
      </tr>`).join('');
    const footerCells = safeColumns.map((column) => {
      if (column.key === 'name') {
        return `<td style="padding:7px 8px;text-align:right;">Σ Разом:</td>`;
      }
      if (column.total) {
        const color = column.key === 'rowCost' ? 'color:#1a7a3c;' : '';
        return `<td style="padding:7px 8px;text-align:right;${color}">${column.total()}</td>`;
      }
      return '<td></td>';
    }).join('');
    const mLabel = mType === 'amount' ? `${mVal.toFixed(2)} грн (${(costP > 0 ? mVal / costP * 100 : 0).toFixed(1)}%)` : `${mVal.toFixed(2)}% (${(costP * mVal / 100).toFixed(2)} грн)`;
    const allergensHtml = allergenBadges.length > 0
      ? `<span>Алергени: <strong>${allergenBadges.join(' · ')}</strong></span>`
      : `<span>Алергени: <strong>—</strong></span>`;
    const compositionTitleHtml = '<h2>Склад страви</h2>';
    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
    <title>Калькуляція: ${name}</title>
    <style>
        * { margin:0; padding:0; box-sizing:border-box; }
        body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 24px; }
        h1 { font-size:20px; color:#c0392b; margin-bottom:4px; }
        h2 { font-size:15px; color:#2c3e50; margin:16px 0 8px; border-bottom:1px solid #ccc; padding-bottom:4px; }
        table { width:100%; border-collapse:collapse; margin-bottom:12px; }
        thead tr { background:#2c3e50; color:white; }
        thead th { padding:7px 8px; text-align:left; font-size:12px; }
        tfoot tr { background:#ecf0f1; font-weight:700; }
        tfoot td { padding:7px 8px; }
        .sheet-top-table { width:100%; border-collapse:separate; border-spacing:0; table-layout:fixed; margin:0 0 8px; }
        .sheet-top-table td { border:0; padding:0; vertical-align:top; }
        .sheet-top-left { padding-right:16px !important; }
        .sheet-top-right { width:280px; }
        .sheet-photo-panel { width:280px; }
        .sheet-photo-placeholder { width:280px; height:280px; border-radius:10px; border:1px dashed #b7c6d6; background:#f4f8fb; color:#6f8599; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; font-size:14px; text-align:center; }
        .sheet-photo-placeholder .icon { font-size:42px; line-height:1; }
        .sheet-top-content { min-height: 280px; min-width:0; max-width:100%; }
        .meta { display:flex; gap:24px; margin:8px 0 16px; flex-wrap:wrap; }
        .meta span { font-size:12px; color:#555; min-width:0; word-break:break-word; }
        .meta .wide { flex:1 1 100%; }
        .meta strong { color:#222; }
        .timing-block { display:grid; grid-template-columns:1fr; gap:6px; margin:0 0 14px; background:#f4f8fb; border:1px solid #d6e1ec; border-radius:7px; padding:10px 12px; }
        .timing-item { font-size:12px; color:#5d6d7e; }
        .timing-item strong { color:#1f2d3a; font-size:13px; }
        .price-block { display:flex; gap:20px; background:#fef9e7; border:1px solid #f39c12; border-radius:6px; padding:12px 16px; margin:8px 0; }
        .price-item { flex:1; text-align:center; }
        .price-item .label { font-size:11px; color:#7f8c8d; margin-bottom:4px; }
        .price-item .value { font-size:16px; font-weight:700; color:#2c3e50; }
        .price-item.final .value { color:#c0392b; font-size:20px; }
        .tech { white-space:pre-wrap; font-size:12px; line-height:1.7; color:#333; background:#f8f9fa; border-radius:4px; padding:10px; }
        .footer { margin-top:20px; font-size:11px; color:#999; border-top:1px solid #eee; padding-top:8px; display:flex; justify-content:space-between; }
        @media screen and (max-width: 860px) {
          .sheet-top-table,
          .sheet-top-table tbody,
          .sheet-top-table tr,
          .sheet-top-table td { display:block; width:100%; }
          .sheet-top-left { padding-right:0 !important; }
          .sheet-top-right { width:100%; margin-top:8px; }
          .sheet-photo-panel { width:100%; max-width:280px; }
          .sheet-photo-placeholder { width:100%; max-width:280px; }
          .sheet-top-content { min-height: 0; }
          .timing-block { grid-template-columns:1fr; }
        }
        @media print { body { padding:12px; print-color-adjust:exact; -webkit-print-color-adjust:exact; } body * { print-color-adjust:exact; -webkit-print-color-adjust:exact; } }
      </style></head><body>
      <table class="sheet-top-table">
        <tbody>
          <tr>
            <td class="sheet-top-left">
              <div class="sheet-top-content">
                <h1>Калькуляційна картка</h1>
                <h2 style="border:none;font-size:18px;color:#222;">${name}</h2>
                <div class="meta">
                  ${catName ? `<span>Категорія: <strong>${catName}</strong></span>` : ''}
                  ${syncCode ? `<span>Код синхронізації: <strong>${syncCode}</strong></span>` : ''}
                  ${wtType ? `<span>Одиниця: <strong>${wtType}</strong></span>` : ''}
                  ${allergensHtml}
                  ${desc ? `<span class="wide">Опис: <strong>${desc}</strong></span>` : ''}
                </div>
                <div class="timing-block">
                  <div class="timing-item">Час приготування: <strong>${formatDurationLabel(cookTime)}</strong></div>
                  <div class="timing-item">Час викладки: <strong>${formatDurationLabel(serveTime)}</strong></div>
                  <div class="timing-item">Вихід фактичний: <strong>${factualOutput.toFixed(3)}</strong></div>
                </div>
              </div>
            </td>
            <td class="sheet-top-right">
              ${photoHtml}
            </td>
          </tr>
        </tbody>
      </table>
      ${compositionTitleHtml}
      <table>
        <thead><tr>
          ${safeColumns.map((column) => `<th style="${column.width ? `width:${column.width};` : ''}text-align:${column.align ?? 'left'};">${column.title}</th>`).join('')}
        </tr></thead>
        <tbody>${rows2}</tbody>
        <tfoot><tr>${footerCells}</tr></tfoot>
      </table>
      <div class="price-block">
        <div class="price-item"><div class="label">Собівартість</div><div class="value">${costP.toFixed(2)} грн</div></div>
        <div class="price-item"><div class="label">Націнка</div><div class="value">${mLabel}</div></div>
        <div class="price-item final"><div class="label">Ціна продажу</div><div class="value">${fPrice.toFixed(2)} грн</div></div>
      </div>
      ${tech ? `<h2>Технологія приготування</h2><div class="tech">${tech}</div>` : ''}
      <div class="footer">
        <span>CRM4Chef — Система управління рестораном</span>
        <span>Дата: ${new Date().toLocaleDateString('uk-UA')}</span>
      </div>
      </body></html>`;
  };

  const buildDishPrintDocument = (d: Dish, forAccounting = false, portions = 1) => {
    const safePortions = Math.max(1, portions);
    const ingRows = d.ingredients.map(i => {
      const unitPrice = i.productId != null
        ? (products.find(p => p.id === i.productId)?.price ?? 0)
        : getSFPrice(i.semiFinishedProductId ?? 0);
      const b = Number(i.bruttoWeight) || Number(i.quantity) || 0;
      const excluded = i.excludeFromDishWeightCalc ?? false;
      const baseLabel = forAccounting
        ? (i.productId != null
            ? getProductAccountingLabel(i.productId, i.productName)
            : getSemiAccountingLabel(i.semiFinishedProductId, i.semiFinishedProductName))
        : (i.productName ?? i.semiFinishedProductName ?? '—');
      return {
        label: `${baseLabel}${excluded ? ' [не рах. вага]' : ''}`,
        notes: i.notes || '',
        brutto: b * safePortions,
        netto: (Number(i.nettoWeight) || 0) * safePortions,
        pieces: i.piecesPerPortion != null ? i.piecesPerPortion * safePortions : null,
        output: (Number(i.output) || 0) * safePortions,
        unitPrice,
        rowCost: unitPrice * b * safePortions,
      };
    });
    const tb = ingRows.reduce((s, r) => s + r.brutto, 0);
    const tn = ingRows.reduce((s, r) => s + r.netto, 0);
    const to = ingRows.reduce((s, r) => s + r.output, 0);
    const columns = forAccounting ? accountingPrintColumns : dishPrintColumns;
    return buildPrintHtml(
      forAccounting ? (d.b52Name?.trim() || d.name) : d.name, forAccounting ? (d.b52Code?.trim() || '') : '', d.categoryName, d.cookingTime, '', d.serveTime || 0, d.description, d.technologyDescription, d.photoUrl,
      ingRows, tb, tn, to, d.costPrice, d.marginType, d.margin, d.finalPrice,
      d.cleanOutput * safePortions, getAllergenBadges(getDishComputedAllergens(d)),
      safePortions,
      columns,
      forAccounting ? 'Назва інгредієнта (B52)' : 'Назва інгредієнта'
    );
  };

  const buildBatchPrintHtml = (title: string, docs: string[]) => {
    const styleMatch = docs[0]?.match(/<style>([\s\S]*?)<\/style>/i);
    const styleContent = styleMatch ? styleMatch[1] : 'body { font-family: Arial, sans-serif; font-size: 13px; color: #222; padding: 18px; }';
    const pages = docs
      .map((doc) => doc.match(/<body>([\s\S]*?)<\/body>/i)?.[1] ?? doc)
      .join('<div style="page-break-after: always;"></div>');

    return `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/><title>${title}</title><style>${styleContent}</style></head><body>${pages}</body></html>`;
  };

  const handlePrintDish = (d: Dish, forAccounting = false) => {
    const portions = Math.max(1, parseFloat(printPortions.replace(',', '.')) || 1);
    openPrintWindow(buildDishPrintDocument(d, forAccounting, portions));
  };

  const handlePrintAllDishesSummary = () => {
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const rows = [...items]
      .sort((a, b) => a.name.localeCompare(b.name, 'uk'))
      .map((dish, index) => {
        const marginPercent = Math.round(
          dish.marginType === 'percent'
            ? Number(dish.margin ?? 0)
            : (Number(dish.costPrice) > 0 ? (Number(dish.margin ?? 0) / Number(dish.costPrice)) * 100 : 0)
        );
        const factualOutput = Number(dish.cleanOutput);
        return `<tr>
          <td style="text-align:center;">${index + 1}</td>
          <td>${escapeHtml(dish.name || '—')}</td>
          <td>${escapeHtml(dish.b52Name?.trim() || '—')}</td>
          <td>${escapeHtml(dish.b52Code?.trim() || '—')}</td>
          <td style="text-align:right;">${Number.isFinite(factualOutput) ? factualOutput.toFixed(3) : '—'}</td>
          <td style="text-align:right;">${Number(dish.costPrice || 0).toFixed(2)}</td>
          <td style="text-align:right;">${marginPercent}%</td>
          <td style="text-align:right;font-weight:700;color:#c0392b;">${Math.round(Number(dish.finalPrice || 0)).toFixed(0)}</td>
        </tr>`;
      })
      .join('');

    const html = `<!DOCTYPE html><html lang="uk"><head><meta charset="utf-8"/>
      <title>Список страв</title>
      <style>
        body{font-family:Arial,sans-serif;padding:18px;color:#1f2937;font-size:10px;}
        h1{margin:0 0 10px;color:#2c3e50;}
        .meta{font-size:10px;color:#4b5563;margin-bottom:4px;}
        table{width:100%;border-collapse:collapse;table-layout:fixed;margin-top:12px;}
        th,td{border:1px solid #d1d5db;padding:6px 6px;font-size:10px;vertical-align:top;word-break:break-word;overflow-wrap:anywhere;white-space:normal;}
        th{background:#2c3e50;color:#fff;text-align:left;}
        tbody tr:nth-child(odd){background:#f8fafc;}
        .col-index{width:30px;text-align:center;}
        .col-name{width:29%;}
        .col-b52-name{width:29%;}
        .col-b52-code{width:66px;}
        .col-output,.col-cost,.col-margin,.col-price{width:58px;text-align:right;}
        @media print { body{padding:12px;print-color-adjust:exact;-webkit-print-color-adjust:exact;} body,*{print-color-adjust:exact;-webkit-print-color-adjust:exact;} }
      </style></head><body>
      <h1>Список страв</h1>
      <div class="meta">Формат: Назва CRM, Назва B52, Код B52, Вихід, Собівартість, Маржа, Ціна</div>
      <div class="meta">Сформовано: ${new Date().toLocaleString('uk-UA')}</div>
      <table>
        <thead>
          <tr>
            <th class="col-index">№</th>
            <th class="col-name">Назва CRM</th>
            <th class="col-b52-name">Назва B52</th>
            <th class="col-b52-code">Код B52</th>
            <th class="col-output">Вихід</th>
            <th class="col-cost">Собівартість</th>
            <th class="col-margin">Маржа</th>
            <th class="col-price">Ціна</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      </body></html>`;

    openPrintWindow(html);
  };

  const resolvePrintIds = (manualIds: number[], categoryIds: number[]) => {
    const byCategory = categoryIds.length === 0
      ? []
      : items.filter((dish) => dish.categoryId != null && categoryIds.includes(dish.categoryId)).map((dish) => dish.id);
    return Array.from(new Set([...manualIds, ...byCategory])).filter((id) => items.some((dish) => dish.id === id));
  };

  const handlePrintSelectedBatch = (manualIds: number[], categoryIds: number[], forAccounting = false) => {
    const ids = resolvePrintIds(manualIds, categoryIds);
    if (ids.length === 0) {
      setError('Не обрано жодної позиції для друку.');
      return;
    }
    const docs = ids
      .map((id) => items.find((dish) => dish.id === id))
      .filter((dish): dish is Dish => dish != null)
      .map((dish) => buildDishPrintDocument(dish, forAccounting));
    openPrintWindow(buildBatchPrintHtml(forAccounting ? 'Бухгалтерський друк страв' : 'Друк страв', docs));
  };

  const handlePrintModal = () => {
    const ingRows = rows.filter(r => r.refId !== '').map(r => {
      const sfObj = r.type === 'semi' ? semiFinished.find(s => s.id === parseInt(r.refId)) : undefined;
      const excluded = sfObj?.isAuxiliaryRecipe || sfObj?.excludeFromDishWeightCalc || false;
      const unitPrice = (r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.price ?? 0)
        : getSFPrice(parseInt(r.refId)));
      const label = r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.name ?? '—')
        : (sfObj?.name ?? '—');
      const b = parseFloat(r.bruttoWeight) || 0;
      return {
        label: `${label}${excluded ? ' [не рах. вага]' : ''}`,
        notes: r.notes,
        brutto: b,
        netto: parseFloat(r.nettoWeight) || 0,
        pieces: r.piecesPerPortion.trim() ? Number(r.piecesPerPortion) : null,
        output: parseFloat(r.output) || 0,
        unitPrice,
        rowCost: unitPrice * b,
      };
    });
    const printTotalBrutto = ingRows.reduce((sum, row) => sum + row.brutto, 0);
    const printTotalNetto = ingRows.reduce((sum, row) => sum + row.netto, 0);
    const printTotalOutput = ingRows.reduce((sum, row) => sum + row.output, 0);
    const catName = form.categoryId ? (categories.find(c => c.id === parseInt(form.categoryId))?.name ?? null) : null;
    const photoUrl = form.photoBlob || form.photoUrl || '';
    const computedAllergens = rows
      .filter(r => r.refId !== '')
      .flatMap((r) => {
        if (r.type === 'product') return products.find(p => p.id === parseInt(r.refId))?.allergens ?? [];
        return semiFinished.find(s => s.id === parseInt(r.refId))?.allergens ?? [];
      })
      .filter((a, idx, arr) => arr.findIndex(x => x.id === a.id) === idx);
    void printDishHtml(buildPrintHtml(
      form.name || 'Нова страва', form.b52Code || '', catName, parseDurationToSeconds(form.cookingTime), form.weightType, parseDurationToSeconds(form.serveTime), form.description, form.technologyDescription, photoUrl,
      ingRows, printTotalBrutto, printTotalNetto, printTotalOutput, cost, form.marginType, marginNum, finalPrice,
      readyDishOutput, getAllergenBadges(computedAllergens),
      1,
      dishPrintColumns,
      'Назва інгредієнта'
    ), form.photoBlob ? '' : form.photoUrl);
  };

  const handlePrintAccountingModal = () => {
    const ingRows = rows.filter(r => r.refId !== '').map(r => {
      const sfObj = r.type === 'semi' ? semiFinished.find(s => s.id === parseInt(r.refId)) : undefined;
      const excluded = sfObj?.isAuxiliaryRecipe || sfObj?.excludeFromDishWeightCalc || false;
      const unitPrice = (r.type === 'product'
        ? (products.find(p => p.id === parseInt(r.refId))?.price ?? 0)
        : getSFPrice(parseInt(r.refId)));
      const label = r.type === 'product'
        ? getProductAccountingLabel(parseInt(r.refId), products.find(p => p.id === parseInt(r.refId))?.name ?? '—')
        : getSemiAccountingLabel(parseInt(r.refId), sfObj?.name ?? '—');
      const b = parseFloat(r.bruttoWeight) || 0;
      return {
        label: `${label}${excluded ? ' [не рах. вага]' : ''}`,
        notes: r.notes,
        brutto: b,
        netto: parseFloat(r.nettoWeight) || 0,
        pieces: r.piecesPerPortion.trim() ? Number(r.piecesPerPortion) : null,
        output: parseFloat(r.output) || 0,
        unitPrice,
        rowCost: unitPrice * b,
      };
    });
    const printTotalBrutto = ingRows.reduce((sum, row) => sum + row.brutto, 0);
    const printTotalNetto = ingRows.reduce((sum, row) => sum + row.netto, 0);
    const printTotalOutput = ingRows.reduce((sum, row) => sum + row.output, 0);
    const catName = form.categoryId ? (categories.find(c => c.id === parseInt(form.categoryId))?.name ?? null) : null;
    const photoUrl = form.photoBlob || form.photoUrl || '';
    const computedAllergens = rows
      .filter(r => r.refId !== '')
      .flatMap((r) => {
        if (r.type === 'product') return products.find(p => p.id === parseInt(r.refId))?.allergens ?? [];
        return semiFinished.find(s => s.id === parseInt(r.refId))?.allergens ?? [];
      })
      .filter((a, idx, arr) => arr.findIndex(x => x.id === a.id) === idx);
    void printDishHtml(buildPrintHtml(
      form.b52Name || form.name || 'Нова страва', form.b52Code || '', catName, parseDurationToSeconds(form.cookingTime), form.weightType, parseDurationToSeconds(form.serveTime), form.description, form.technologyDescription, photoUrl,
      ingRows, printTotalBrutto, printTotalNetto, printTotalOutput, cost, form.marginType, marginNum, finalPrice,
      readyDishOutput, getAllergenBadges(computedAllergens),
      1,
      accountingPrintColumns,
      'Назва інгредієнта (B52)'
    ), form.photoBlob ? '' : form.photoUrl);
  };

  const compressImage = (file: File, maxSize = 1200, quality = 0.85): Promise<File> =>
    new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          if (width > height) { height = Math.round(height * maxSize / width); width = maxSize; }
          else { width = Math.round(width * maxSize / height); height = maxSize; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
        canvas.toBlob(blob => resolve(blob ? new File([blob], file.name, { type: 'image/jpeg' }) : file), 'image/jpeg', quality);
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawFile = e.target.files?.[0]; if (!rawFile) return;
    const file = rawFile.size > 500 * 1024 ? await compressImage(rawFile) : rawFile;
    if (editingId) {
      setUploadingPhoto(true);
      try {
        const result = await dispatch(uploadDishPhoto({ id: editingId, file })).unwrap();
        setForm(f => ({ ...f, photoUrl: result.url, photoBlob: undefined }));
      } catch (err: any) { setError(extractError(err, 'Помилка завантаження фото')); }
      finally { setUploadingPhoto(false); }
    } else {
      if (form.photoBlob) URL.revokeObjectURL(form.photoBlob);
      const blob = URL.createObjectURL(file);
      pendingPhotoRef.current = file;
      setForm(f => ({ ...f, photoBlob: blob, photoUrl: '' }));
    }
    if (photoInputRef.current) photoInputRef.current.value = '';
  };

  const uploadPreparationStepPhoto = async (stepIndex: number, file: File) => {
    if (!editingId) throw new Error('Спочатку збережіть страву.');
    const body = new FormData();
    body.append('file', file);
    const response = await api.post<{ url: string }>(
      `/recipe-preparation-steps/dish/${editingId}/${stepIndex}/photo`,
      body,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.url;
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Назва є обов'язковою"); return; }
    if (rows.some(row => row.refId && row.piecesPerPortion.trim() && (!Number.isFinite(Number(row.piecesPerPortion)) || Number(row.piecesPerPortion) <= 0))) {
      setError('Штук на порцію: вкажіть додатне число або залиште поле порожнім.'); return;
    }
    if (saveArchiveSnapshot && !archiveEffectiveFromUtc) { setError('Вкажіть дату архівної копії.'); return; }
    const archiveOperationGroupId = operatingGroup.groupId;
    setSaving(true); setError(null);
    try {
    const effectiveMarginType: 'percent' = 'percent';
    const effectiveMargin = lockedFinalPrice != null && cost > 0
      ? Math.max(0, ((roundToNonNegativePrice(lockedFinalPrice) / cost) - 1) * 100)
      : Math.max(0, Math.round(marginNum));
    const normalizedServingsCount = form.servingsCount.trim() ? Math.max(0, Math.round(parseFloat(form.servingsCount) || 0)) : null;
    const payload = {
      name: form.name.trim(),
      b52Code: form.b52Code.trim(),
      b52Name: form.b52Name.trim(),
      categoryId: form.categoryId ? parseInt(form.categoryId) : null,
      description: form.description,
      cookingTime: parseDurationToSeconds(form.cookingTime),
      serveTime: parseDurationToSeconds(form.serveTime),
      technologyDescription: form.technologyDescription,
      preparationSteps: form.preparationSteps,
      inventoryRequired: false,
      photoUrl: form.photoUrl,
      netWeight: totalNetto,
      grossWeight: totalBrutto,
      cleanOutput: readyDishOutput,
      servingsCount: normalizedServingsCount,
      marginType: effectiveMarginType,
      margin: effectiveMargin,
      saveArchiveSnapshot,
      archiveEffectiveFromUtc: saveArchiveSnapshot ? archiveEffectiveFromUtc || new Date().toISOString().slice(0, 10) : null,
      ingredients: rows.filter(r => r.refId !== '').map(r => ({
        productId: r.type === 'product' ? parseInt(r.refId) : null,
        semiFinishedProductId: r.type === 'semi' ? parseInt(r.refId) : null,
        quantity: parseFloat(r.bruttoWeight) || 0,
        bruttoWeight: parseFloat(r.bruttoWeight) || 0,
        nettoWeight: parseFloat(r.nettoWeight) || 0,
        piecesPerPortion: r.piecesPerPortion.trim() ? Number(r.piecesPerPortion) : null,
        output: parseFloat(r.output) || 0,
        notes: r.notes.trim(),
        operationNotes: r.operationNotes.trim(),
        preparationOperationId: r.preparationOperationId ? parseInt(r.preparationOperationId) : null,
        processingMethodId: r.processingMethodId ? parseInt(r.processingMethodId) : null,
        processingTemperatureC: r.processingTemperatureC.trim() ? parseFloat(r.processingTemperatureC) || null : null,
      })),
    };

    let savedId = editingId;
    if (editingId) {
      const updatedDish = await dispatch(updateDish({ id: editingId, data: payload })).unwrap();
      savedId = updatedDish.id;
    } else {
      const res = await dispatch(createDish(payload)).unwrap();
      savedId = res.id;
    }

    if (pendingPhotoRef.current && savedId) {
      await dispatch(uploadDishPhoto({ id: savedId, file: pendingPhotoRef.current })).unwrap();
      pendingPhotoRef.current = null;
    }

    let archiveRebuildNotice: string | null = null;
    if (saveArchiveSnapshot && savedId) {
      const currentContext = operatingGroup.isCurrent(archiveOperationGroupId);
      const unavailableNotice = currentContext
        ? getArchiveRebuildUnavailableNotice(archiveOperationGroupId)
        : getHistoricalWriteOffUnavailableNotice(operatingGroup.groupId, operatingGroup.changedInAnotherTab, operatingGroup.ready)
          || 'Робочий контекст змінився під час збереження; історичні списання не змінювалися.';
      if (unavailableNotice) {
        archiveRebuildNotice = unavailableNotice;
      } else {
        const controller = new AbortController();
        archiveRebuildAbort.current = controller;
        archiveRebuildIsCurrent.current = () => operatingGroup.isCurrent(archiveOperationGroupId);
        try {
          const preview = await api.get(`/dishes/${savedId}/archive-write-off-preview`, {
            params: { fromDate: archiveEffectiveFromUtc },
            signal: controller.signal,
          });
          if (!operatingGroup.isCurrent(archiveOperationGroupId)) {
            archiveRebuildNotice = 'Робочий контекст змінився; історичні списання не змінювалися.';
          } else {
          const affectedCount = Number(preview.data?.completedOrdersCount ?? 0);
          if (affectedCount > 0) {
            const shouldRebuild = window.confirm(
              `Архів збережено. Знайдено ${affectedCount} завершених замовлень із цією стравою починаючи з ${new Date(`${archiveEffectiveFromUtc}T00:00:00`).toLocaleDateString('uk-UA')}. Перебудувати їхні списання за відповідними архівними калькуляціями?`
            );
            if (!shouldRebuild) {
              archiveRebuildNotice = 'Архів збережено. Історичні списання не змінювалися.';
            } else {
              let cursor: number | null = null;
              let rebuiltCount = 0;
              const rebuildErrors: Array<{ eventOrderId: number; message: string }> = [];
              let remainingCount = affectedCount;
              while (remainingCount > 0) {
                if (!operatingGroup.isCurrent(archiveOperationGroupId)) {
                  archiveRebuildNotice = 'Робочий контекст змінився; подальшу перебудову зупинено. Перевірте результат останнього запиту.';
                  break;
                }
                const batchResponse: { data: ArchiveWriteOffRebuildBatch } = await api.post<ArchiveWriteOffRebuildBatch>(`/dishes/${savedId}/rebuild-archive-write-offs`, {
                  fromDate: archiveEffectiveFromUtc,
                  afterOrderId: cursor,
                }, {
                  signal: controller.signal,
                });
                rebuiltCount += Number(batchResponse.data.rebuiltCount ?? 0);
                rebuildErrors.push(...(batchResponse.data.errors ?? []));
                remainingCount = Number(batchResponse.data.remainingCount ?? 0);
                cursor = batchResponse.data.nextAfterOrderId ?? null;
                if (remainingCount > 0 && cursor == null) {
                  throw new Error('Сервер не повернув позицію для продовження пакетної перебудови.');
                }
              }
              const failedOrders = rebuildErrors.slice(0, 5).map(item => `#${item.eventOrderId}`).join(', ');
              if (!archiveRebuildNotice) {
                archiveRebuildNotice = `Архів збережено. Перебудовано списань: ${rebuiltCount}.${rebuildErrors.length > 0 ? ` Помилок: ${rebuildErrors.length} (${failedOrders}${rebuildErrors.length > 5 ? ', …' : ''}).` : ''}`;
              }
            }
          }
          }
        } catch (rebuildError: any) {
          archiveRebuildNotice = operatingGroup.isCurrent(archiveOperationGroupId)
            ? `Архів і калькуляцію збережено, але перевірити або перебудувати історичні списання не вдалося: ${rebuildError?.response?.data?.message || rebuildError?.message || 'невідома помилка'}`
            : 'Архів збережено. Робочий контекст змінився; подальшу перебудову зупинено. Перевірте результат останнього запиту.';
        } finally {
          if (archiveRebuildAbort.current === controller) {
            archiveRebuildAbort.current = null;
            archiveRebuildIsCurrent.current = null;
          }
        }
      }
    }

    await dispatch(fetchDishes());
    closeModal();
    if (archiveRebuildNotice) window.alert(archiveRebuildNotice);
    } catch (err: any) {
    setError(err?.message || 'Не вдалося зберегти страву');
    } finally {
    setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Видалити страву?')) return;
    try {
      setError(null);
      await dispatch(deleteDish(id)).unwrap();
      setSelectedIds((prev) => prev.filter((x) => x !== id));
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося видалити страву'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) {
      setError('Спочатку виберіть страви для видалення.');
      return;
    }
    if (!window.confirm(`Видалити вибрані страви (${selectedIds.length})?`)) return;

    try {
      setError(null);
      const result = await dispatch(bulkDeleteDishes(selectedIds)).unwrap();
      setSelectedIds((prev) => prev.filter((id) => !result.deletedIds.includes(id)));
      if (result.errors.length > 0) {
        setError(result.errors.join(' '));
      }
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося видалити вибрані страви'));
    }
  };

  const handleRecalculateCosts = async () => {
    const confirmed = window.confirm(
      `Перерахувати собівартість для ${semiFinished.length} напівфабрикатів і ${items.length} страв? ` +
      'Операція зберігає знімок попередніх значень, тож останній перерахунок можна буде скасувати кнопкою поруч.'
    );
    if (!confirmed) return;

    try {
      setError(null);
      setImportMsg(null);
      setRecalculatingCosts(true);
      const result = await dispatch(recalculateDishAndSemiCosts()).unwrap();
      await dispatch(fetchSemiFinished());
      await dispatch(fetchDishes());
      setImportMsg(`Собівартість перераховано. Н/Ф: ${result.semiFinishedUpdated}, страв: ${result.dishesUpdated}.`);
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося перерахувати собівартість Н/Ф та страв'));
    } finally {
      setRecalculatingCosts(false);
    }
  };

  const handleUndoRecalculateCosts = async () => {
    if (!window.confirm('Скасувати останній перерахунок собівартості Н/Ф і страв?')) return;

    try {
      setError(null);
      setImportMsg(null);
      setUndoingRecalculateCosts(true);
      const result = await dispatch(undoLastDishAndSemiCostRecalculation()).unwrap();
      await dispatch(fetchSemiFinished());
      await dispatch(fetchDishes());
      setImportMsg(`Останній перерахунок скасовано. Н/Ф: ${result.restoredSemiFinished}, страв: ${result.restoredDishes}.`);
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося скасувати останній перерахунок'));
    } finally {
      setUndoingRecalculateCosts(false);
    }
  };

  const normalizeLookup = (value: unknown) => String(value ?? '').trim().toLowerCase();

  const parseFlag = (value: unknown, fallback = false) => {
    const normalized = normalizeLookup(value);
    if (!normalized) return fallback;
    return ['так', 'true', '1', 'yes', 'y'].includes(normalized);
  };

  const parseNumberCell = (value: unknown): number | null => {
    return parseRecipeImportNumber(value);
  };

  const buildDishImportPayload = (
    dish: Dish,
    ingredients: Array<{
      productId: number | null;
      semiFinishedProductId: number | null;
      quantity: number;
      bruttoWeight: number;
      nettoWeight: number;
      piecesPerPortion?: number | null;
      output: number;
      notes?: string;
      operationNotes?: string | null;
      preparationOperationId?: number | null;
      processingMethodId?: number | null;
      processingTemperatureC?: number | null;
    }> = dish.ingredients
  ) => ({
    name: dish.name,
    b52Code: dish.b52Code ?? '',
    b52Name: dish.b52Name ?? '',
    categoryId: dish.categoryId,
    description: dish.description,
    cookingTime: dish.cookingTime,
    serveTime: dish.serveTime || 0,
    technologyDescription: dish.technologyDescription,
    preparationSteps: dish.preparationSteps ?? [],
    inventoryRequired: dish.inventoryRequired,
    photoUrl: dish.photoUrl,
    netWeight: dish.netWeight,
    grossWeight: dish.grossWeight,
    cleanOutput: dish.cleanOutput,
    servingsCount: dish.servingsCount ?? null,
    marginType: dish.marginType,
    margin: dish.margin,
    ingredients: ingredients.map((ingredient) => ({
      productId: ingredient.productId,
      semiFinishedProductId: ingredient.semiFinishedProductId,
      quantity: ingredient.quantity,
      bruttoWeight: ingredient.bruttoWeight,
      nettoWeight: ingredient.nettoWeight,
      piecesPerPortion: ingredient.piecesPerPortion ?? null,
      output: ingredient.output,
      notes: ingredient.notes ?? '',
      operationNotes: ingredient.operationNotes ?? '',
      preparationOperationId: ingredient.preparationOperationId ?? null,
      processingMethodId: ingredient.processingMethodId ?? null,
      processingTemperatureC: ingredient.processingTemperatureC ?? null,
    })),
  });

  const resolveDishByRow = (pool: Dish[], row: any) => {
    const id = parseInt(String(row['Код (ID)'] ?? row['id'] ?? row['Страва ID'] ?? '').trim(), 10);
    const name = String(row['Назва'] ?? row['Страва'] ?? '').trim();
    const b52Code = String(row['Код B52'] ?? row['b52Code'] ?? row['Код B52 страви'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(pool, dish => normalizeLookup(dish.b52Code) === normalizeLookup(b52Code));
    if (Number.isInteger(id) && id > 0) return pool.find(dish => dish.id === id);
    return name ? uniqueRecipeMatch(pool, dish => normalizeLookup(dish.name) === normalizeLookup(name)) : undefined;
  };

  const resolveProductIngredient = (row: any) => {
    const id = parseInt(String(row['ID інгредієнта'] ?? row['ProductId'] ?? '').trim(), 10);
    const name = String(row['Назва інгредієнта'] ?? row['ProductName'] ?? '').trim();
    const b52Code = String(row['Код B52 інгредієнта'] ?? row['ProductB52Code'] ?? '').trim();
    const b52Name = String(row['Назва B52 інгредієнта'] ?? row['ProductB52Name'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(products, product => normalizeLookup(product.b52Code) === normalizeLookup(b52Code));
    if (b52Name) return uniqueRecipeMatch(products, product => normalizeLookup(product.b52Name) === normalizeLookup(b52Name));
    if (Number.isInteger(id) && id > 0) return products.find(product => product.id === id);
    return name ? uniqueRecipeMatch(products, product => normalizeLookup(product.name) === normalizeLookup(name)) : undefined;
  };

  const resolveSemiIngredient = (row: any) => {
    const id = parseInt(String(row['ID інгредієнта'] ?? row['SemiFinishedProductId'] ?? '').trim(), 10);
    const name = String(row['Назва інгредієнта'] ?? row['SemiFinishedProductName'] ?? '').trim();
    const b52Code = String(row['Код B52 інгредієнта'] ?? row['SemiFinishedB52Code'] ?? '').trim();
    const b52Name = String(row['Назва B52 інгредієнта'] ?? row['SemiFinishedB52Name'] ?? '').trim();

    if (b52Code) return uniqueRecipeMatch(semiFinished, item => normalizeLookup(item.b52Code) === normalizeLookup(b52Code));
    if (b52Name) return uniqueRecipeMatch(semiFinished, item => normalizeLookup(item.b52Name) === normalizeLookup(b52Name));
    if (Number.isInteger(id) && id > 0) return semiFinished.find(item => item.id === id);
    return name ? uniqueRecipeMatch(semiFinished, item => normalizeLookup(item.name) === normalizeLookup(name)) : undefined;
  };

  const resolvePreparationOperation = (row: any) => {
    const id = parseInt(String(row['Операція ID'] ?? '').trim(), 10);
    const name = String(row['Операція'] ?? '').trim();
    if (Number.isInteger(id) && id > 0) return preparationOperations.find(operation => operation.id === id);
    return name ? uniqueRecipeMatch(preparationOperations, operation => normalizeLookup(operation.name) === normalizeLookup(name)) : undefined;
  };

  const resolveProcessingMethod = (row: any) => {
    const id = parseInt(String(row['Метод обробки ID'] ?? '').trim(), 10);
    const name = String(row['Метод обробки'] ?? '').trim();
    if (Number.isInteger(id) && id > 0) return processingMethods.find(method => method.id === id);
    return name ? uniqueRecipeMatch(processingMethods, method => normalizeLookup(method.name) === normalizeLookup(name)) : undefined;
  };

  const handleExport = () => {
    const data = items.map(d => ({
      'Код (ID)': d.id,
      'Назва': d.name,
      'Код B52': d.b52Code ?? '',
      'Назва B52': d.b52Name ?? '',
      'Категорія': d.categoryName ?? '',
      'Опис': d.description ?? '',
      'Час приготування (сек або хв,сек)': d.cookingTime,
      'Час на видачу (сек або хв,сек)': d.serveTime ?? 0,
      'Технологія': d.technologyDescription ?? '',
      'Кроки приготування JSON': JSON.stringify(d.preparationSteps ?? []),
      'Потрібно списання': d.inventoryRequired ? 'Так' : 'Ні',
      'Брутто': d.grossWeight, 'Нетто': d.netWeight, 'Вихід': d.cleanOutput, 'Людей': d.servingsCount ?? '',
      'Собівартість (грн)': d.costPrice,
      'Маржа': d.marginType === 'percent' ? d.margin : (d.costPrice > 0 ? ((d.margin / d.costPrice) * 100) : 0), 'Тип маржі': '%',
      'Ціна продажу (грн)': d.finalPrice,
      'Склад': d.ingredients.map(i => `${i.productName ?? i.semiFinishedProductName}(${i.output}${i.notes ? `, ${i.notes}` : ''})`).join('; '),
    }));
    const compositionRows = items.flatMap((dish) => dish.ingredients.map((ingredient) => {
      const product = ingredient.productId != null ? products.find((item) => item.id === ingredient.productId) : null;
      const semi = ingredient.semiFinishedProductId != null ? semiFinished.find((item) => item.id === ingredient.semiFinishedProductId) : null;
      return {
        'Страва ID': dish.id,
        'Страва': dish.name,
        'Код B52 страви': dish.b52Code ?? '',
        'Назва B52 страви': dish.b52Name ?? '',
        'Тип інгредієнта': ingredient.productId != null ? 'Товар' : 'Н/Ф',
        'ID інгредієнта': ingredient.productId ?? ingredient.semiFinishedProductId ?? '',
        'Назва інгредієнта': ingredient.productName ?? ingredient.semiFinishedProductName ?? '',
        'Код B52 інгредієнта': product?.b52Code ?? semi?.b52Code ?? '',
        'Назва B52 інгредієнта': product?.b52Name ?? semi?.b52Name ?? '',
        'Кількість': ingredient.quantity,
        'Брутто': ingredient.bruttoWeight,
        'Нетто': ingredient.nettoWeight,
        'Штук на порцію': ingredient.piecesPerPortion ?? '',
        'Вихід': ingredient.output,
        'Нотатка': ingredient.notes ?? '',
        'Операційна нотатка': ingredient.operationNotes ?? '',
        'Операція ID': ingredient.preparationOperationId ?? '',
        'Операція': ingredient.preparationOperationName ?? '',
        'Метод обробки ID': ingredient.processingMethodId ?? '',
        'Метод обробки': ingredient.processingMethodName ?? '',
        'Температура обробки °C': ingredient.processingTemperatureC ?? '',
      };
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [8, 28, 16, 28, 20, 28, 16, 16, 18, 10, 10, 10, 14, 10, 10, 16, 40].map(wch => ({ wch }));
    const compositionSheet = XLSX.utils.json_to_sheet(compositionRows);
    compositionSheet['!cols'] = [{ wch: 10 }, { wch: 28 }, { wch: 16 }, { wch: 28 }, { wch: 16 }, { wch: 12 }, { wch: 28 }, { wch: 16 }, { wch: 28 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 28 }];
    const lookupRows = [
      ...products.map(product => ({
        'Тип': 'Товар', 'ID': product.id, 'Назва': product.name, 'Код B52': product.b52Code ?? '',
        'Назва B52': product.b52Name ?? '', 'Одиниця': product.weightType ?? '',
      })),
      ...semiFinished.map(semi => ({
        'Тип': 'Н/Ф', 'ID': semi.id, 'Назва': semi.name, 'Код B52': semi.b52Code ?? '',
        'Назва B52': semi.b52Name ?? '', 'Одиниця': semi.weightType ?? '',
      })),
      ...categories.map(category => ({
        'Тип': 'Категорія страви', 'ID': category.id, 'Назва': category.name, 'Код B52': '', 'Назва B52': '', 'Одиниця': '',
      })),
      ...preparationOperations.map(operation => ({
        'Тип': 'Операція', 'ID': operation.id, 'Назва': operation.name, 'Код B52': '', 'Назва B52': '', 'Одиниця': '',
      })),
      ...processingMethods.map(method => ({
        'Тип': 'Метод обробки', 'ID': method.id, 'Назва': method.name, 'Код B52': '', 'Назва B52': '',
        'Одиниця': method.defaultTemperatureC == null ? '' : `${method.defaultTemperatureC} °C`,
      })),
    ];
    const promptSheet = XLSX.utils.aoa_to_sheet([
      ['CRM4Chef recipe workbook draft v1'],
      ['Заповнюйте лише аркуші «Страви» та «Склад»; збережіть назви колонок і аркушів.'],
      ['Використовуйте ID із «Довідник» для інгредієнтів, операцій і методів обробки; не підбирайте неоднозначні позиції лише за назвою.'],
      ['Кроки приготування JSON: масив об’єктів із title, instructions та optional estimatedMinutes (ціле число хвилин).'],
      ['Не створюйте ручні алергени/нутрієнти, фото-URL, HACCP температури, строки зберігання чи інші непогоджені правила.'],
      ['Після генерації файл пройде перевірку CRM; помилки потрібно виправити до збереження. Імпорт кількох API-запитів наразі не є атомарним.'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Страви');
    XLSX.utils.book_append_sheet(wb, compositionSheet, 'Склад');
    XLSX.utils.book_append_sheet(wb, promptSheet, 'Інструкція AI');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lookupRows), 'Довідник');
    XLSX.writeFile(wb, `dishes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleExportRecipeWorkbook = () => {
    const dishHeaders = [
      'Name', 'Category', 'CookingTime', 'NetWeight', 'GrossWeight', 'CostPrice', 'Margin',
      'FinalPrice', 'Allergens', 'CleanOutput', 'Description', 'TechnologyDescription',
      'PreparationStepsJson',
    ];
    const semiHeaders = [
      'Name', 'Category', 'WeightType', 'NetWeight', 'GrossWeight', 'CostPrice',
      'Description', 'Allergens', 'IsAuxiliaryRecipe', 'PreparationStepsJson',
    ];
    const dishItemHeaders = [
      'DishName', 'IngredientType', 'IngredientName', 'BruttoWeight', 'NettoWeight',
      'Output', 'Quantity', 'Notes', 'PiecesPerPortion', 'OperationNotes',
      'PreparationOperationName', 'ProcessingMethodName', 'ProcessingTemperatureC',
    ];
    const semiItemHeaders = [
      'SemiFinishedName', 'IngredientType', 'IngredientName', 'BruttoWeight', 'NettoWeight',
      'Output', 'Quantity', 'Notes', 'OperationNotes', 'PreparationOperationName',
      'ProcessingMethodName', 'ProcessingTemperatureC',
    ];
    const lookupRows = [
      ...products.map(product => ({ Type: 'Product', ID: product.id, Name: product.name, Unit: product.weightType ?? '' })),
      ...semiFinished.map(semi => ({ Type: 'SemiFinished', ID: semi.id, Name: semi.name, Unit: semi.weightType ?? '' })),
      ...categories.map(category => ({ Type: 'DishCategory', ID: category.id, Name: category.name, Unit: '' })),
      ...semiFinishedCategories.map(category => ({ Type: 'SemiFinishedCategory', ID: category.id, Name: category.name, Unit: '' })),
      ...preparationOperations.map(operation => ({ Type: 'PreparationOperation', ID: operation.id, Name: operation.name, Unit: '' })),
      ...processingMethods.map(method => ({ Type: 'ProcessingMethod', ID: method.id, Name: method.name, Unit: '' })),
    ];
    const instructions = [
      ['CRM4Chef recipe workbook draft v1'],
      ['Fill the four recipe data sheets and the Dish Visuals sheet. Keep the exact English sheet and column names. Leave IDs, categories, operations and methods anchored to exact entries in Lookup; include component names exactly as listed.'],
      ['Treat this as a proposed recipe for the user to review. For each meaningful prepared component that should be a SemiFinished, create your own proposed SemiFinished recipe in this workbook even if a similar existing SemiFinished appears in Lookup. Do not silently substitute an existing SemiFinished for the proposal; the user will decide whether to keep the generated recipe or replace it with an existing one. Do not create unnecessary or unrelated SemiFinished recipes.'],
      ['For every proposed SemiFinished, add one recipe row in SemiFinished and its complete ingredient rows in SemiFinishedItems; connect it to the parent dish/recipe with IngredientType SemiFinished and the exact same Name in DishItems/SemiFinishedItems. Use exact Product names from Lookup for ingredients. Existing SemiFinished entries in Lookup are available as alternatives for the user to substitute during review, not as a reason to omit the proposed recipe.'],
      ['New SemiFinished recipes are ORDINARY weight-based preparations by default: set IsAuxiliaryRecipe to false and WeightType to kg (unless the requested recipe explicitly requires another unit). Being a component of a dish does NOT make a recipe auxiliary. Meat cooked sous-vide, sauces, purees and other batch preparations remain ordinary SemiFinished recipes with their actual yield in NetWeight. Use IsAuxiliaryRecipe true ONLY when the user explicitly requests a per-plate/per-serving auxiliary set; only then is its effective unit a counted set rather than kg. Never infer this flag from the word component or semi-finished.'],
      ['Use one row per recipe and one row per ingredient. Do not invent catalog IDs, categories, operations, or processing methods. Choose an exact SemiFinishedCategory from Lookup when one fits; if none is suitable, leave Category blank for the user to classify rather than inventing one. A new SemiFinished exists only in this draft until the user previews and explicitly applies the whole workbook in CRM.'],
      ['For EVERY dish and semi-finished recipe, create ordered, practical preparation steps in PreparationStepsJson (aim for 3–8 when the recipe warrants it; include at least one meaningful step). Explain the actual kitchen actions clearly enough for a cook to follow; include preparation, cooking/assembly and finishing where applicable. Never leave the field blank or use an empty array.'],
      ['PreparationStepsJson must be a JSON array of objects: [{"title":"...","instructions":"...","estimatedMinutes":null}]. Each step requires title and instructions; estimatedMinutes is optional and must be null if unknown. PhotoUrl is permitted only for an existing CRM-managed photo reference; do not invent URLs.'],
      ['For each dish, create a photorealistic food-photography image of that exact finished dish using the available image-generation capability; do not stop at writing an image prompt if image generation is available. Base appearance and garnishes on the dish name, its actual ingredients and serving instructions. Do not add ingredients, garnishes, doneness, or cooking details that are absent from or contradict the recipe. Use polished fine-dining plating, appetizing realistic textures, deliberate composition, soft dramatic studio lighting, a 45-degree close food-photography view and shallow depth of field, adapting the plating to the dish rather than copying a fixed reference.'],
      ['In Dish Visuals, add one row per dish with its exact Name in DishName, a detailed English ImageGenerationPrompt tailored to the actual recipe, and embed the generated image anchored inside that dish row’s GeneratedImage cell (column C). If image generation or embedding is unavailable, still provide the detailed ready-to-use prompt and state that the image was not generated; never fabricate an image or URL. On Apply, CRM imports an embedded image from this sheet as the dish photo after validating and normalizing it. Preview never saves images. The user can replace the imported reference with a real photo later through the CRM recipe editor.'],
      ['Do not provide Allergens, nutrients, food-safety/HACCP rules, storage periods, or unapproved temperatures. Leave Allergens blank and ProcessingTemperatureC blank unless a validated existing method supplies it.'],
      ['Costs and prices are recalculated by CRM. Preview runs against a transaction and rolls back. Apply is enabled only when every validation succeeds.'],
      ['Versioned JSON contract is in «Schema v1». Use schemaVersion crm4chef.recipe-draft.v1. The server resolves exact names and rejects unknown/ambiguous references. Map each recipe ingredient to its corresponding workbook component sheet.'],
    ];
    const recipeSchema = {
      type: 'object',
      additionalProperties: false,
      required: ['name', 'category', 'ingredients', 'preparationSteps'],
      properties: {
        name: { type: 'string', minLength: 1 },
        category: { type: 'string', description: 'Exact category name from Lookup; may be empty.' },
        description: { type: 'string' },
        technologyDescription: { type: 'string' },
        preparationSteps: {
          type: 'array',
          minItems: 1,
          maxItems: 50,
          items: {
            type: 'object',
            additionalProperties: false,
            required: ['title', 'instructions'],
            properties: {
              title: { type: 'string', minLength: 1, maxLength: 120 },
              instructions: { type: 'string', minLength: 1, maxLength: 3000 },
              estimatedMinutes: { type: ['integer', 'null'], minimum: 0, maximum: 1440 },
              photoUrl: { anyOf: [{ type: 'null' }, { type: 'string', pattern: '^/uploads/recipe-steps/[a-fA-F0-9]{32}\\.jpg$' }] },
            },
          },
        },
        ingredients: {
          type: 'array',
          minItems: 1,
          items: {
            type: 'object',
            additionalProperties: false,
            required: ['type', 'name', 'quantity', 'bruttoWeight', 'nettoWeight'],
            properties: {
              type: { enum: ['Product', 'SemiFinished'] },
              id: { type: ['integer', 'null'], minimum: 1 },
              name: { type: 'string', minLength: 1, description: 'Exact Lookup name, or exact unique Name from this workbook’s semiFinished rows; server rejects unknown or ambiguous matches.' },
              quantity: { type: 'number', minimum: 0 },
              bruttoWeight: { type: 'number', minimum: 0 },
              nettoWeight: { type: 'number', minimum: 0 },
              output: { type: 'number', minimum: 0 },
              piecesPerPortion: { type: ['number', 'null'], exclusiveMinimum: 0 },
              notes: { type: 'string' },
              operationNotes: { type: 'string' },
              preparationOperationName: { type: 'string' },
              processingMethodName: { type: 'string' },
              processingTemperatureC: { type: ['number', 'null'], description: 'Leave blank unless provided by a validated existing processing method.' },
            },
          },
        },
      },
    };
    const schema = {
      $schema: 'https://json-schema.org/draft/2020-12/schema',
      $id: 'crm4chef.recipe-draft.v1',
      type: 'object',
      additionalProperties: false,
      required: ['schemaVersion', 'dishes', 'semiFinished'],
      properties: {
        schemaVersion: { const: 'crm4chef.recipe-draft.v1' },
        dishes: { type: 'array', items: recipeSchema },
        semiFinished: {
          type: 'array',
          items: {
            ...recipeSchema,
            properties: {
              ...recipeSchema.properties,
              isAuxiliaryRecipe: {
                type: 'boolean', default: false,
                description: 'Map to IsAuxiliaryRecipe. False for ordinary batch preparations; true only for an explicitly requested per-serving auxiliary set.',
              },
              weightType: {
                enum: ['kg', 'g', 'l', 'ml', 'pcs'], default: 'kg',
                description: 'Map to WeightType. Use kg for ordinary weight-based semi-finished recipes.',
              },
              netWeight: {
                type: 'number', exclusiveMinimum: 0,
                description: 'Map to NetWeight: actual recipe batch yield in WeightType, not a count of auxiliary sets.',
              },
            },
          },
        },
      },
    };
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([dishHeaders]), 'Dishes');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([semiHeaders]), 'SemiFinished');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([dishItemHeaders]), 'DishItems');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([semiItemHeaders]), 'SemiFinishedItems');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(instructions), 'Prompt');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['DishName', 'ImageGenerationPrompt', 'GeneratedImage'],
    ]), 'Dish Visuals');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(lookupRows), 'Lookup');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
      ['SchemaVersion', 'JSON Schema'],
      ['crm4chef.recipe-draft.v1', JSON.stringify(schema)],
    ]), 'Schema v1');
    XLSX.writeFile(wb, `crm4chef-recipe-draft-v1_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const handleRecipeWorkbookSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setRecipeWorkbookFile(null);
    setRecipeWorkbookPreview(null);
    setImportMsg(null);
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post('/import/recipe-workbook/preview', formData);
      setRecipeWorkbookFile(file);
      setRecipeWorkbookPreview(response.data);
    } catch (err: any) {
      setImportMsg(extractError(err, 'Попередній перегляд AI-рецепта не пройшов.'));
    } finally {
      setImporting(false);
    }
  };

  const handleApplyRecipeWorkbook = async () => {
    if (!recipeWorkbookFile || !recipeWorkbookPreview?.canApply) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', recipeWorkbookFile);
      const response = await api.post('/import/recipe-workbook/apply', formData);
      setImportMsg(`Імпорт застосовано: створено ${response.data.imported}, оновлено ${response.data.updated}.`);
      setRecipeWorkbookFile(null);
      setRecipeWorkbookPreview(null);
      await Promise.all([
        dispatch(fetchDishes()),
        dispatch(fetchSemiFinished()),
        dispatch(fetchProducts()),
      ]);
    } catch (err: any) {
      setImportMsg(extractError(err, 'Не вдалося застосувати AI-рецепт.'));
    } finally {
      setImporting(false);
    }
  };

  const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImportMsg(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        setImporting(true);
        const data = new Uint8Array(ev.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const masterSheet = wb.Sheets['Страви'] ?? wb.Sheets[wb.SheetNames[0]];
        const compositionSheet = wb.Sheets['Склад'];
        const rowsIn: any[] = XLSX.utils.sheet_to_json(masterSheet, { defval: '' });
        const compositionRows: any[] = compositionSheet ? XLSX.utils.sheet_to_json(compositionSheet, { defval: '' }) : [];
        const hasComposition = compositionRows.length > 0;
        const importErrors: string[] = [];
        const numericFields = ['Брутто', 'Нетто', 'Вихід', 'Кількість', 'Штук на порцію'];
        const dishNames = new Set<string>();
        rowsIn.forEach((row, index) => {
          const rowNumber = index + 2;
          const dishName = String(row['Назва'] ?? '').trim();
          const dishKey = normalizeLookup(dishName);
          if (!dishName) importErrors.push(`Страви, рядок ${rowNumber}: не вказано назву.`);
          else if (dishNames.has(dishKey)) importErrors.push(`Страви, рядок ${rowNumber}: назва «${dishName}» повторюється у файлі.`);
          dishNames.add(dishKey);
          for (const field of ['Брутто', 'Нетто', 'Вихід', 'Маржа', 'Ціна продажу (грн)', 'Ціна продажу', 'Ціна']) {
            const value = row[field];
            const parsed = parseNumberCell(value);
            if (String(value ?? '').trim() && parsed == null)
              importErrors.push(`Страви, рядок ${rowNumber}: «${field}» має містити коректне число.`);
            else if (parsed != null && parsed < 0)
              importErrors.push(`Страви, рядок ${rowNumber}: «${field}» не може бути від’ємним.`);
          }
          const categoryName = String(row['Категорія'] ?? '').trim();
          if (categoryName && !categories.some(category => normalizeLookup(category.name) === normalizeLookup(categoryName)))
            importErrors.push(`Страви, рядок ${rowNumber}: категорію «${categoryName}» не знайдено.`);
          const stepsCell = row['Кроки приготування JSON'];
          if (String(stepsCell ?? '').trim() && parseRecipePreparationSteps(String(stepsCell)) == null)
            importErrors.push(`Страви, рядок ${rowNumber}: «Кроки приготування JSON» має некоректний формат.`);
        });
        compositionRows.forEach((row, index) => {
          const rowNumber = index + 2;
          const dishName = String(row['Страва'] ?? row['Страва ID'] ?? '').trim();
          const ingredientName = String(row['Назва інгредієнта'] ?? '').trim();
          const existingDish = resolveDishByRow(items, row);
          const matchingNewDishes = rowsIn.filter(master => normalizeLookup(master['Назва']) === normalizeLookup(dishName));
          if (!existingDish && matchingNewDishes.length !== 1)
            importErrors.push(`Склад, рядок ${rowNumber}: страву «${dishName || 'не вказано'}» не знайдено або її відповідність неоднозначна.`);
          if (!ingredientName) importErrors.push(`Склад, рядок ${rowNumber}: не вказано інгредієнт.`);

          const kind = normalizeLookup(row['Тип інгредієнта']);
          if (kind && kind !== 'товар' && kind !== 'н/ф')
            importErrors.push(`Склад, рядок ${rowNumber}: невідомий тип інгредієнта «${kind}». Вкажіть «Товар» або «Н/Ф».`);
          const product = kind === 'н/ф' ? undefined : resolveProductIngredient(row);
          const semi = kind === 'товар' ? undefined : resolveSemiIngredient(row);
          if (ingredientName && !product && !semi)
            importErrors.push(`Склад, рядок ${rowNumber}: інгредієнт «${ingredientName}» не знайдений або має неоднозначну відповідність.`);
          else if (ingredientName && !kind && product && semi)
            importErrors.push(`Склад, рядок ${rowNumber}: «${ingredientName}» є і товаром, і Н/Ф — вкажіть тип інгредієнта.`);
          if ((String(row['Операція ID'] ?? '').trim() || String(row['Операція'] ?? '').trim()) && !resolvePreparationOperation(row))
            importErrors.push(`Склад, рядок ${rowNumber}: підготовчу операцію не знайдено або відповідність неоднозначна.`);
          if ((String(row['Метод обробки ID'] ?? '').trim() || String(row['Метод обробки'] ?? '').trim()) && !resolveProcessingMethod(row))
            importErrors.push(`Склад, рядок ${rowNumber}: метод обробки не знайдено або відповідність неоднозначна.`);
          const temperatureCell = row['Температура обробки °C'];
          if (String(temperatureCell ?? '').trim() && parseNumberCell(temperatureCell) == null)
            importErrors.push(`Склад, рядок ${rowNumber}: температура обробки має бути числом.`);

          for (const field of numericFields) {
            const value = row[field];
            const parsed = parseNumberCell(value);
            if (String(value ?? '').trim() && parsed == null)
              importErrors.push(`Склад, рядок ${rowNumber}: «${field}» має містити коректне число.`);
            else if (parsed != null && parsed < 0)
              importErrors.push(`Склад, рядок ${rowNumber}: «${field}» не може бути від’ємним.`);
          }
          if ((parseNumberCell(row['Кількість']) ?? 0) <= 0 && (parseNumberCell(row['Брутто']) ?? 0) <= 0)
            importErrors.push(`Склад, рядок ${rowNumber}: вкажіть додатну «Кількість» або «Брутто».`);
        });
        if (rowsIn.length === 0) importErrors.push('Файл не містить рядків страв.');
        if (importErrors.length) {
          setImportMsg(`Імпорт скасовано: ${importErrors.slice(0, 8).join(' ')}${importErrors.length > 8 ? ` Ще помилок: ${importErrors.length - 8}.` : ''}`);
          return;
        }
        let created = 0, updated = 0, compositionsUpdated = 0, finalPriceAligned = 0, skipped = 0;
        const dishPool = [...items];
        const explicitFinalPriceByDishId = new Map<number, number>();
        for (const row of rowsIn) {
          const name = String(row['Назва'] ?? '').trim();
          if (!name) { skipped++; continue; }
          const existing = resolveDishByRow(dishPool, row);
          const catName = String(row['Категорія'] ?? '').trim();
          const catId = catName
            ? (categories.find(c => normalizeLookup(c.name) === normalizeLookup(catName))?.id ?? null)
            : (existing?.categoryId ?? null);
          const mt: 'percent' = 'percent';
          const rawMarginCell = String(row['Маржа'] ?? '').trim();
          const importedFinalPrice = parseNumberCell(row['Ціна продажу (грн)'] ?? row['Ціна продажу'] ?? row['Ціна']);
          const existingCostPrice = parseFloat(String(existing?.costPrice ?? '0')) || 0;
          let importedMargin = rawMarginCell !== '' ? (parseNumberCell(row['Маржа']) ?? 0) : (existing?.margin ?? 0);
          let importedMarginType = mt;
          if (importedFinalPrice != null && importedFinalPrice > 0 && existingCostPrice > 0) {
            // Explicit final price from import file always has priority over margin value.
            importedMargin = Math.max(0, ((importedFinalPrice / existingCostPrice) - 1) * 100);
          }
          const payload = {
            name,
            b52Code: String(row['Код B52'] ?? row['b52Code'] ?? existing?.b52Code ?? '').trim(),
            b52Name: String(row['Назва B52'] ?? row['b52Name'] ?? existing?.b52Name ?? '').trim(),
            categoryId: catId,
            description: String(row['Опис'] ?? existing?.description ?? ''),
            cookingTime: parseDurationToSeconds(String(row['Час приготування (сек або хв,сек)'] ?? row['Час приготування (хв)'] ?? existing?.cookingTime ?? '0')),
            serveTime: parseDurationToSeconds(String(row['Час на видачу (сек або хв,сек)'] ?? row['Час на видачу (хв)'] ?? existing?.serveTime ?? '0')),
            technologyDescription: String(row['Технологія'] ?? existing?.technologyDescription ?? ''),
            preparationSteps: String(row['Кроки приготування JSON'] ?? '').trim()
              ? parseRecipePreparationSteps(String(row['Кроки приготування JSON'])) ?? []
              : (existing?.preparationSteps ?? []),
            inventoryRequired: parseFlag(row['Потрібно списання'], existing?.inventoryRequired ?? false),
            photoUrl: existing?.photoUrl ?? '',
            netWeight: parseNumberCell(row['Нетто'] ?? existing?.netWeight) ?? 0,
            grossWeight: parseNumberCell(row['Брутто'] ?? existing?.grossWeight) ?? 0,
            cleanOutput: parseNumberCell(row['Вихід'] ?? existing?.cleanOutput) ?? 0,
            servingsCount: parseInt(String(row['Людей:'] ?? row['Людей'] ?? row['Кількість людей'] ?? row['Порцій'] ?? existing?.servingsCount ?? '').trim(), 10) || null,
            marginType: importedMarginType,
            margin: importedMargin,
            ingredients: hasComposition ? [] : (existing?.ingredients ?? []),
          };

          try {
            const result = existing
              ? await dispatch(updateDish({ id: existing.id, data: payload })).unwrap()
              : await dispatch(createDish(payload)).unwrap();
            const existingIndex = dishPool.findIndex((dish) => dish.id === result.id);
            if (existingIndex >= 0) dishPool[existingIndex] = result;
            else dishPool.push(result);
            if (importedFinalPrice != null && importedFinalPrice > 0) {
              explicitFinalPriceByDishId.set(result.id, importedFinalPrice);
            }
            if (existing) updated++;
            else created++;
          } catch {
            skipped++;
          }
        }

        if (hasComposition) {
          const parentIds = Array.from(new Set(compositionRows
            .map((row) => resolveDishByRow(dishPool, row)?.id ?? null)
            .filter((id): id is number => id != null)));

          for (const parentId of parentIds) {
            const parent = dishPool.find((dish) => dish.id === parentId);
            if (!parent) continue;
            const parentRows = compositionRows.filter((row) => resolveDishByRow(dishPool, row)?.id === parentId);
            const resolvedIngredients = parentRows.map((row) => {
              const type = normalizeLookup(row['Тип інгредієнта']);
              const resolvedProduct = type === 'товар' || !type ? resolveProductIngredient(row) : null;
              const resolvedSemi = type === 'н/ф' || (!type && !resolvedProduct) ? resolveSemiIngredient(row) : null;
              if (!resolvedProduct && !resolvedSemi)
                throw new Error(`Інгредієнт «${String(row['Назва інгредієнта'] ?? '').trim()}» не знайдено або відповідність неоднозначна.`);
              const piecesCell = row['Штук на порцію'] ?? row['PiecesPerPortion'];
              const pieces = parseNumberCell(piecesCell);
              if (String(piecesCell ?? '').trim() && (pieces == null || pieces <= 0))
                throw new Error(`Страва «${parent.name}»: Штук на порцію має бути додатним числом.`);
              return {
                productId: resolvedProduct?.id ?? null,
                semiFinishedProductId: resolvedSemi?.id ?? null,
                quantity: parseNumberCell(row['Кількість'] ?? row['Брутто']) ?? 0,
                bruttoWeight: parseNumberCell(row['Брутто'] ?? row['Кількість']) ?? 0,
                nettoWeight: parseNumberCell(row['Нетто']) ?? 0,
                piecesPerPortion: pieces,
                output: parseNumberCell(row['Вихід']) ?? 0,
                notes: String(row['Нотатка'] ?? '').trim(),
                operationNotes: String(row['Операційна нотатка'] ?? row['OperationNotes'] ?? '').trim(),
                preparationOperationId: resolvePreparationOperation(row)?.id ?? null,
                processingMethodId: resolveProcessingMethod(row)?.id ?? null,
                processingTemperatureC: parseNumberCell(row['Температура обробки °C']),
              };
            });

            try {
              const updatedDish = await dispatch(updateDish({
                id: parent.id,
                data: buildDishImportPayload(parent, resolvedIngredients),
              })).unwrap();
              const parentIndex = dishPool.findIndex((dish) => dish.id === updatedDish.id);
              if (parentIndex >= 0) dishPool[parentIndex] = updatedDish;
              compositionsUpdated++;
            } catch {
              skipped++;
            }
          }
        }

        if (explicitFinalPriceByDishId.size > 0) {
          for (const [dishId, targetFinalPrice] of Array.from(explicitFinalPriceByDishId.entries())) {
            const currentDish = dishPool.find((dish) => dish.id === dishId);
            if (!currentDish) continue;
            const currentCost = Number(currentDish.costPrice) || 0;
            if (currentCost <= 0 || targetFinalPrice <= 0) continue;

            const requiredMargin = Math.max(0, ((targetFinalPrice / currentCost) - 1) * 100);
            if (Math.abs((Number(currentDish.margin) || 0) - requiredMargin) < 0.0001) continue;

            try {
              const repricedDish = await dispatch(updateDish({
                id: dishId,
                data: buildDishImportPayload({ ...currentDish, marginType: 'percent', margin: requiredMargin }),
              })).unwrap();
              const currentIndex = dishPool.findIndex((dish) => dish.id === repricedDish.id);
              if (currentIndex >= 0) dishPool[currentIndex] = repricedDish;
              finalPriceAligned++;
            } catch {
              skipped++;
            }
          }
        }
        await dispatch(fetchDishes());
        setImportMsg(`Імпортовано. Створено: ${created}. Оновлено: ${updated}. Складів оновлено: ${compositionsUpdated}. Цін зафіксовано: ${finalPriceAligned}. Пропущено: ${skipped}.`);
      } catch { setImportMsg('Помилка читання файлу.'); }
      finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
    };
    reader.readAsArrayBuffer(file);
  };

  const photoDisplay = form.photoBlob || (form.photoUrl ? imgSrc(form.photoUrl) : null);

  const panelStyle: React.CSSProperties = {
    background: 'var(--crm-surface)',
    color: 'var(--crm-text)',
    border: '1px solid var(--crm-border)',
    borderRadius: 'var(--crm-radius-md)',
    boxShadow: 'var(--crm-shadow-sm)',
  };
  const buttonStyle = (tone: 'primary' | 'success' | 'danger' | 'warning' | 'muted' | 'accent'): React.CSSProperties => ({
    background: `var(--crm-${tone === 'muted' ? 'surface-alt' : tone === 'primary' ? 'primary' : tone === 'accent' ? 'accent' : `${tone}-bg`})`,
    color: `var(--crm-${tone === 'muted' ? 'text' : tone === 'primary' ? 'primary-contrast' : tone === 'accent' ? 'accent-contrast' : tone})`,
    border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone === 'primary' ? 'primary' : tone === 'accent' ? 'accent' : tone})`,
    borderRadius: 'var(--crm-radius-sm)',
    padding: '8px 12px',
  });
  const inp: React.CSSProperties = { border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', padding: '7px 10px', width: '100%', boxSizing: 'border-box', fontSize: '14px', background: 'var(--crm-input-bg)', color: 'var(--crm-text)' };
  const lbl: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--crm-text)', fontSize: '13px' };
  const th: React.CSSProperties = { padding: '9px 10px', background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', textAlign: 'left', fontWeight: 600, fontSize: '13px', whiteSpace: 'nowrap' };
  const td: React.CSSProperties = { padding: '6px 8px', fontSize: '13px', color: 'var(--crm-text)', borderBottom: '1px solid var(--crm-table-row-border)' };
  const categorySearchItems = categories.map((category) => ({ id: String(category.id), name: category.name }));
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredDishes = items.filter((d) => {
    if (!normalizedSearch) return true;
    if (d.name.toLowerCase().includes(normalizedSearch)) return true;
    return d.ingredients.some((ingredient) =>
      (ingredient.productName || '').toLowerCase().includes(normalizedSearch) ||
      (ingredient.semiFinishedProductName || '').toLowerCase().includes(normalizedSearch)
    );
  });
  const sortedDishes = [...filteredDishes].sort((a, b) => {
    switch (sortBy) {
      case 'name-desc': return b.name.localeCompare(a.name, 'uk');
      case 'price-asc': return a.finalPrice - b.finalPrice;
      case 'price-desc': return b.finalPrice - a.finalPrice;
      case 'cost-asc': return a.costPrice - b.costPrice;
      case 'cost-desc': return b.costPrice - a.costPrice;
      case 'category-asc': return (a.categoryName ?? '').localeCompare(b.categoryName ?? '', 'uk');
      case 'category-desc': return (b.categoryName ?? '').localeCompare(a.categoryName ?? '', 'uk');
      case 'name-asc':
      default: return a.name.localeCompare(b.name, 'uk');
    }
  });
  const getSortArrow = (ascKey: DishSort, descKey: DishSort) => {
    if (sortBy === ascKey) return ' ↑';
    if (sortBy === descKey) return ' ↓';
    return '';
  };
  const toggleSort = (ascKey: DishSort, descKey: DishSort) => {
    setSortBy((prev) => (prev === ascKey ? descKey : ascKey));
  };
  const totalPages = Math.max(1, Math.ceil(sortedDishes.length / PAGE_SIZE));
  const page = Math.min(currentPage, totalPages);
  const pagedDishes = sortedDishes.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const pageStart = sortedDishes.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(page * PAGE_SIZE, sortedDishes.length);
  const detailsPortionsValue = Math.max(0, parseFloat(detailsPortions.replace(',', '.')) || 0);
  const detailsRequiredCooking = detailsDish ? calcRequiredTime(detailsDish.cookingTime, detailsPortionsValue) : 0;
  const detailsRequiredServe = detailsDish ? calcRequiredTime(detailsDish.serveTime, detailsPortionsValue) : 0;
  const detailsWorkStartSeconds = parseClockToSeconds(detailsWorkStart);
  const detailsWorkEndSeconds = parseClockToSeconds(detailsWorkEnd);
  const detailsWorkPortionsValue = Math.max(0, parseFloat(detailsWorkPortions.replace(',', '.')) || 0);
  const detailsWorkDurationSeconds = detailsWorkStartSeconds != null && detailsWorkEndSeconds != null
    ? (detailsWorkEndSeconds >= detailsWorkStartSeconds
      ? detailsWorkEndSeconds - detailsWorkStartSeconds
      : (24 * 3600 - detailsWorkStartSeconds) + detailsWorkEndSeconds)
    : 0;
  const detailsWorkSecondsPerPortion = detailsWorkPortionsValue > 0 ? detailsWorkDurationSeconds / detailsWorkPortionsValue : 0;
  const allVisibleSelected = pagedDishes.length > 0 && pagedDishes.every((d) => selectedIds.includes(d.id));
  const selectedCategoryCount = printCategoryIds.length;
  const resolvedPrintPreviewIds = resolvePrintIds(selectedIds, printCategoryIds);
  const printColumnOptions: Array<{ key: DishPrintColumnKey; label: string }> = [
    { key: 'name', label: 'Назва інгредієнта' },
    { key: 'brutto', label: 'Брутто' },
    { key: 'netto', label: 'Нетто' },
    { key: 'notes', label: 'Нотатки' },
    { key: 'output', label: 'Вихід' },
    { key: 'unitPrice', label: 'Ціна/од' },
    { key: 'rowCost', label: 'Вартість' },
  ];
  const toggleDishPrintColumn = (key: DishPrintColumnKey) =>
    setDishPrintColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const toggleAccountingPrintColumn = (key: DishPrintColumnKey) =>
    setAccountingPrintColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  const selectedDishesForReplace = React.useMemo(
    () => items.filter((dish) => selectedIds.includes(dish.id)),
    [items, selectedIds]
  );
  const ingredientTableColumns = React.useMemo(() => {
    const columns: Array<{ key: string; title: string; width?: string }> = [
      { key: 'name', title: 'Інгредієнт / брутто' },
      { key: 'netto', title: 'Нетто', width: '88px' },
      { key: 'pieces', title: 'Шт./порц.', width: '90px' },
      { key: 'output', title: 'Вихід', width: '88px' },
    ];
    if (ingredientColumns.price) columns.push({ key: 'price', title: 'Ціна/од', width: '80px' });
    if (ingredientColumns.cost) columns.push({ key: 'cost', title: 'Вартість', width: '92px' });
    if (ingredientColumns.notes) columns.push({ key: 'notes', title: 'Нотатки картки', width: '150px' });
    if (ingredientColumns.processing) {
      columns.push(
        { key: 'processing', title: 'Обробка', width: '130px' },
        { key: 'temperature', title: '°C', width: '70px' }
      );
    }
    columns.push(
      { key: 'operation', title: 'Операція', width: '140px' },
      { key: 'operationNotes', title: 'Інфо по операції', width: '180px' },
      { key: 'actions', title: '', width: '36px' }
    );
    return columns;
  }, [ingredientColumns.cost, ingredientColumns.notes, ingredientColumns.price, ingredientColumns.processing]);
  const ingredientNameByEncodedId = React.useMemo(() => {
    const map = new Map<string, string>();
    for (const ingredient of allIngredients) map.set(ingredient.id, ingredient.name);
    return map;
  }, [allIngredients]);
  const commonIngredientsForReplace = React.useMemo(() => {
    if (selectedDishesForReplace.length === 0) return [] as Array<{ id: string; name: string }>;

    const allIngredientIds = new Set<string>();
    selectedDishesForReplace.forEach((dish) => {
      dish.ingredients.forEach((ingredient) => {
        if (ingredient.productId != null) allIngredientIds.add(`p:${ingredient.productId}`);
        if (ingredient.semiFinishedProductId != null) allIngredientIds.add(`s:${ingredient.semiFinishedProductId}`);
      });
    });

    return Array.from(allIngredientIds.values())
      .map((id) => ({ id, name: ingredientNameByEncodedId.get(id) ?? id }))
      .sort((a, b) => a.name.localeCompare(b.name, 'uk'));
  }, [ingredientNameByEncodedId, selectedDishesForReplace]);

  const togglePrintCategory = (categoryId: number) => {
    setPrintCategoryIds((prev) => prev.includes(categoryId)
      ? prev.filter((id) => id !== categoryId)
      : [...prev, categoryId]);
  };

  const saveCurrentPrintSelection = () => {
    const name = printSelectionName.trim();
    if (!name) {
      setError('Вкажіть назву набору друку.');
      return;
    }
    const next: SavedPrintSelection = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name,
      selectedIds: [...selectedIds],
      categoryIds: [...printCategoryIds],
    };
    setSavedPrintSelections((prev) => [next, ...prev]);
    setPrintSelectionName('');
  };

  const applySavedSelection = (selection: SavedPrintSelection) => {
    setSelectedIds(resolvePrintIds(selection.selectedIds, []));
    setPrintCategoryIds(selection.categoryIds);
  };

  const removeSavedSelection = (selectionId: string) => {
    setSavedPrintSelections((prev) => prev.filter((selection) => selection.id !== selectionId));
  };

  const openReplaceIngredientsModal = () => {
    if (selectedDishesForReplace.length === 0) {
      setError('Спочатку виберіть страви для масової заміни інгредієнтів.');
      return;
    }
    const firstIngredient = commonIngredientsForReplace[0]?.id ?? '';
    setReplaceFromIngredientId(firstIngredient);
    setReplaceToIngredientId('');
    setReplaceIngredientsModalOpen(true);
  };

  const handleReplaceIngredients = async () => {
    if (selectedDishesForReplace.length === 0) {
      setError('Немає вибраних страв для заміни інгредієнтів.');
      return;
    }
    if (!replaceFromIngredientId) {
      setError('Оберіть інгредієнт, який потрібно замінити.');
      return;
    }
    if (!replaceToIngredientId) {
      setError('Оберіть новий інгредієнт.');
      return;
    }
    if (replaceFromIngredientId === replaceToIngredientId) {
      setError('Інгредієнт заміни має відрізнятися від вихідного.');
      return;
    }

    const from = decodeIngredientId(replaceFromIngredientId);
    const to = decodeIngredientId(replaceToIngredientId);
    const toProductId = to.type === 'product' ? parseInt(to.refId, 10) : null;
    const toSemiFinishedProductId = to.type === 'semi' ? parseInt(to.refId, 10) : null;

    setReplacingIngredients(true);
    setError(null);
    try {
      let updatedCount = 0;
      for (const dish of selectedDishesForReplace) {
        let changed = false;
        const nextIngredients = dish.ingredients.map((ingredient) => {
          const isMatch = from.type === 'product'
            ? ingredient.productId === parseInt(from.refId, 10)
            : ingredient.semiFinishedProductId === parseInt(from.refId, 10);
          if (!isMatch) return ingredient;
          changed = true;
          return {
            ...ingredient,
            productId: toProductId,
            semiFinishedProductId: toSemiFinishedProductId,
          };
        });

        if (!changed) continue;

        await dispatch(updateDish({
          id: dish.id,
          data: buildDishImportPayload(dish, nextIngredients),
        })).unwrap();
        updatedCount++;
      }

      await dispatch(fetchDishes());
      setReplaceIngredientsModalOpen(false);
      window.alert(`Заміна інгредієнтів виконана. Оновлено страв: ${updatedCount}.`);
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося виконати заміну інгредієнтів.'));
    } finally {
      setReplacingIngredients(false);
    }
  };

  const toggleDeleteCategory = (categoryId: number) => {
    setDeleteCategoryIds((prev) => prev.includes(categoryId)
      ? prev.filter((id) => id !== categoryId)
      : [...prev, categoryId]);
  };

  const getDishIdsByCategories = (categoryIds: number[]) => items
    .filter((dish) => dish.categoryId != null && categoryIds.includes(dish.categoryId))
    .map((dish) => dish.id);

  const handleDeleteByCategories = async () => {
    if (deleteCategoryIds.length === 0) {
      setError('Оберіть хоча б одну категорію для видалення.');
      return;
    }

    const targetIds = getDishIdsByCategories(deleteCategoryIds);
    if (targetIds.length === 0) {
      setError('У вибраних категоріях немає страв для видалення.');
      return;
    }

    if (!window.confirm(`Видалити всі страви у вибраних категоріях? Кількість: ${targetIds.length}`)) return;

    try {
      setError(null);
      const result = await dispatch(bulkDeleteDishes(targetIds)).unwrap();
      setSelectedIds((prev) => prev.filter((id) => !result.deletedIds.includes(id)));
      if (result.errors.length > 0) {
        setError(result.errors.join(' '));
      } else {
        setDeleteByCategoryModalOpen(false);
      }
    } catch (err: any) {
      setError(extractError(err, 'Не вдалося видалити страви за категоріями'));
    }
  };

  return (
    <div className="page-shell page-shell--wide management-recipe-page dish-management-page" style={{ maxWidth: '1400px' }}>
      <div className="dish-list-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid var(--crm-accent)', paddingBottom: '8px' }}>
        <h2 style={{ color: 'var(--crm-text)', margin: 0 }}>Управління стравами</h2>
        <button onClick={openNew} style={{ ...buttonStyle('success'), padding: '9px 18px', cursor: 'pointer', fontWeight: 600 }}>+ Додати</button>
      </div>

      <div className="dish-list-toolbar" style={{ display: 'flex', gap: '10px', marginTop: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={handleExport} style={{ ...buttonStyle('success'), cursor: 'pointer', fontWeight: 500 }}>⬇ Експорт Excel</button>
        <button type="button" onClick={() => importRef.current?.click()} disabled={importing}
          style={{ ...buttonStyle('primary'), cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 500, opacity: importing ? 0.7 : 1 }}>
          ⬆ {importing ? 'Імпорт...' : 'Імпорт Excel'}
        </button>
        <input ref={importRef} type="file" accept=".xlsx,.xls" aria-label="Файл імпорту страв"
          onChange={handleImportFile} disabled={importing} style={{ display: 'none' }} />
        <button type="button" onClick={handleExportRecipeWorkbook}
          style={{ ...buttonStyle('accent'), cursor: 'pointer', fontWeight: 500 }}>
          ⬇ AI-шаблон рецепта v1
        </button>
        <button type="button" onClick={() => recipeWorkbookRef.current?.click()} disabled={importing}
          style={{ ...buttonStyle('primary'), cursor: importing ? 'not-allowed' : 'pointer', fontWeight: 500 }}>
          {importing ? 'Перевірка...' : 'Попередній перегляд AI-рецепта'}
        </button>
        <input ref={recipeWorkbookRef} type="file" accept=".xlsx,.xlsm"
          aria-label="Файл AI recipe workbook" onChange={handleRecipeWorkbookSelect}
          disabled={importing} style={{ display: 'none' }} />
        <button
          type="button"
          onClick={handleRecalculateCosts}
          disabled={recalculatingCosts || undoingRecalculateCosts}
          style={{ ...buttonStyle(recalculatingCosts || undoingRecalculateCosts ? 'muted' : 'success'), cursor: recalculatingCosts || undoingRecalculateCosts ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          {recalculatingCosts ? 'Перерахунок...' : 'Перерахувати собівартість Н/Ф + Страв'}
        </button>
        <button
          type="button"
          onClick={handleUndoRecalculateCosts}
          disabled={undoingRecalculateCosts || recalculatingCosts}
          style={{ ...buttonStyle(undoingRecalculateCosts || recalculatingCosts ? 'muted' : 'accent'), cursor: undoingRecalculateCosts || recalculatingCosts ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          {undoingRecalculateCosts ? 'Скасування...' : '↩ Скасувати останній перерахунок'}
        </button>
        <button
          type="button"
          onClick={() => setB52ModalOpen(true)}
          style={{ ...buttonStyle('accent'), cursor: 'pointer', fontWeight: 500 }}
        >
          B52 Довідник
        </button>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Пошук страви..."
          aria-label="Пошук страви"
          style={{ ...inp, maxWidth: '320px' }}
        />
        <button
          type="button"
          onClick={handleBulkDelete}
          disabled={selectedIds.length === 0}
          style={{ ...buttonStyle(selectedIds.length === 0 ? 'muted' : 'danger'), cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          Видалити вибрані ({selectedIds.length})
        </button>
        <button
          type="button"
          onClick={() => setDeleteByCategoryModalOpen(true)}
          style={{ ...buttonStyle('danger'), cursor: 'pointer', fontWeight: 500 }}
        >
          Видалити за категоріями
        </button>
        <button
          type="button"
          onClick={() => setPrintManageModalOpen(true)}
          style={{ ...buttonStyle('muted'), cursor: 'pointer', fontWeight: 500 }}
        >
          Керування друком
        </button>
        <button
          type="button"
          onClick={handlePrintAllDishesSummary}
          style={{ ...buttonStyle('primary'), cursor: 'pointer', fontWeight: 500 }}
        >
          🖨 Друк списку страв
        </button>
        <button
          type="button"
          onClick={openReplaceIngredientsModal}
          disabled={selectedIds.length === 0}
          style={{ ...buttonStyle(selectedIds.length === 0 ? 'muted' : 'accent'), cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 500 }}
        >
          Заміна інгредієнтів
        </button>
      </div>
      {importMsg && <div style={{ ...panelStyle, marginTop: '8px', padding: '10px', fontSize: '14px', background: importMsg.startsWith('Імпортовано') ? 'var(--crm-success-bg)' : 'var(--crm-danger-bg)', color: importMsg.startsWith('Імпортовано') ? 'var(--crm-success)' : 'var(--crm-danger)' }}>{importMsg}</div>}
      {recipeWorkbookPreview && <section style={{ ...panelStyle, marginTop: 8, padding: 12 }} aria-live="polite">
        <strong>{recipeWorkbookPreview.canApply ? 'Попередній перегляд пройшов' : 'Попередній перегляд виявив помилки'}</strong>
        <p>Книга: {recipeWorkbookFile?.name ?? 'невідомо'} · створення: {recipeWorkbookPreview.imported} · оновлення: {recipeWorkbookPreview.updated}. Перевірка виконана у транзакції з відкатом; дані поки не збережені.</p>
        {!!recipeWorkbookPreview.errors.length && <ul>{recipeWorkbookPreview.errors.slice(0, 20).map((issue, index) => <li key={`${index}:${issue}`}>{issue}</li>)}</ul>}
        {recipeWorkbookPreview.canApply && <button type="button" onClick={handleApplyRecipeWorkbook} disabled={importing}
          style={{ ...buttonStyle('success'), cursor: importing ? 'not-allowed' : 'pointer' }}>
          Застосувати перевірену книгу
        </button>}
      </section>}
      {error && <div style={{ ...panelStyle, marginTop: '8px', padding: '10px', fontSize: '14px', background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)' }}>{error}</div>}

      {/* List table */}
      {loading ? <p>Завантаження...</p> : (
        <table className="management-recipe-list dish-list-table" style={{ ...panelStyle, width: '100%', borderCollapse: 'collapse', overflow: 'hidden', marginTop: '20px' }}>
          <thead>
            <tr>
              <th style={th}>
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={(e) => {
                    const visibleIds = pagedDishes.map((d) => d.id);
                    setSelectedIds((prev) => e.target.checked
                      ? Array.from(new Set([...prev, ...visibleIds]))
                      : prev.filter((id) => !visibleIds.includes(id)));
                  }}
                />
              </th>
              <th style={th}>Фото</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('name-asc', 'name-desc')}
                title="Сортувати за назвою"
              >
                Назва{getSortArrow('name-asc', 'name-desc')}
              </th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('category-asc', 'category-desc')}
                title="Сортувати за категорією"
              >
                Категорія{getSortArrow('category-asc', 'category-desc')}
              </th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('cost-asc', 'cost-desc')}
                title="Сортувати за собівартістю"
              >
                Собівартість{getSortArrow('cost-asc', 'cost-desc')}
              </th>
              <th style={th}>Маржа</th>
              <th
                style={{ ...th, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => toggleSort('price-asc', 'price-desc')}
                title="Сортувати за ціною продажу"
              >
                Ціна продажу{getSortArrow('price-asc', 'price-desc')}
              </th>
              <th style={th}>Вихід</th>
              <th style={th}>Дії</th>
            </tr>
          </thead>
          <tbody>
            {pagedDishes.map((d, i) => (
              <tr key={d.id} style={{ background: i % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
                <td data-label="Обрати" style={td}>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(d.id)}
                    onChange={(e) => setSelectedIds((prev) => e.target.checked ? [...prev, d.id] : prev.filter((id) => id !== d.id))}
                  />
                </td>
                <td data-label="Фото" style={{ ...td, width: '64px' }}>
                  {d.photoUrl
                    ? <AuthorizedApiImage src={d.photoUrl} alt={d.name}
                        style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-border)', display: 'block' }}
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                    : <div style={{ width: '52px', height: '52px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text-muted)', borderRadius: 'var(--crm-radius-sm)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '22px' }}>🍽</div>}
                </td>
                <td data-label="Назва" style={{ ...td, fontWeight: 500 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    <button
                      type="button"
                      onClick={() => openDetailsModal(d)}
                      style={{ background: 'none', border: 'none', padding: 0, textAlign: 'left', color: 'var(--crm-text)', cursor: 'pointer', fontWeight: 600 }}
                    >
                      {d.name}
                    </button>
                    {(d.b52Code || d.b52Name) && (
                      <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                        {[d.b52Code ? `Код: ${d.b52Code}` : '', d.b52Name ? `B52: ${d.b52Name}` : ''].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>
                </td>
                <td data-label="Категорія" style={td}>{d.categoryName ?? '—'}</td>
                <td data-label="Собівартість" style={{ ...td, color: 'var(--crm-text-muted)' }}>{d.costPrice.toFixed(2)} грн</td>
                <td data-label="Маржа" style={td}><span style={{ fontSize: '12px', background: 'var(--crm-input-bg-muted)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '10px', padding: '2px 8px' }}>{Math.round(d.marginType === 'percent' ? Number(d.margin ?? 0) : (Number(d.costPrice) > 0 ? (Number(d.margin ?? 0) / Number(d.costPrice)) * 100 : 0))}%</span></td>
                <td data-label="Ціна продажу" style={{ ...td, fontWeight: 700, color: 'var(--crm-danger)', fontSize: '14px' }}>{Math.round(d.finalPrice)} грн</td>
                <td data-label="Вихід" style={td}>{d.cleanOutput.toFixed(3)}</td>
                <td data-label="Керування" style={{ ...td }}>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button onClick={() => openEdit(d)} style={{ ...buttonStyle('primary'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>Редагувати</button>
                    <button onClick={() => handlePrintDish(d)} title="Друкувати калькуляцію" aria-label="Друкувати калькуляцію" style={{ ...buttonStyle('accent'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>🖨</button>
                    <button onClick={() => handlePrintDish(d, true)} title="Друк для бухгалтера" style={{ ...buttonStyle('success'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>Для бухгалтера</button>
                    <button onClick={() => handleDelete(d.id)} style={{ ...buttonStyle('danger'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>Видалити</button>
                  </div>
                </td>
              </tr>
            ))}
            {sortedDishes.length === 0 && <tr><td colSpan={9} style={{ ...td, padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Страви відсутні</td></tr>}
          </tbody>
        </table>
      )}
      {!loading && sortedDishes.length > 0 && (
        <div className="dish-list-pagination" style={{ marginTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ color: 'var(--crm-text-muted)', fontSize: '13px' }}>
            Показано {pageStart}-{pageEnd} з {sortedDishes.length}
          </span>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              style={{ ...buttonStyle(page <= 1 ? 'muted' : 'primary'), padding: '6px 12px', cursor: page <= 1 ? 'not-allowed' : 'pointer' }}
            >
              ← Назад
            </button>
            <span style={{ minWidth: '100px', textAlign: 'center', color: 'var(--crm-text)', fontSize: '13px' }}>
              Сторінка {page} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              style={{ ...buttonStyle(page >= totalPages ? 'muted' : 'primary'), padding: '6px 12px', cursor: page >= totalPages ? 'not-allowed' : 'pointer' }}
            >
              Вперед →
            </button>
          </div>
        </div>
      )}

      {detailsModalOpen && detailsDish && (
        <div className="dish-details-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2090, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="dish-details-modal" style={{ width: '100%', maxWidth: '660px', maxHeight: '88vh', overflowY: 'auto', background: 'var(--crm-surface)', border: '1px solid var(--crm-border)', borderRadius: '10px', boxShadow: 'var(--crm-shadow-sm)', padding: '18px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div className="dish-details-header dish-details-modal__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>{detailsDish.name}</h3>
              <div className="dish-details-actions dish-details-modal__actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <SendPdfToTelegramButton endpoint={`/dishes/${detailsDish.id}/send-telegram`} />
                <button
                  type="button"
                  onClick={() => {
                    const d = detailsDish;
                    const allergens = getDishComputedAllergens(d).map((a) => `${getAllergenIcon(a.name)} ${a.name}`).join(', ') || '—';
                    const photoHtml = d.photoUrl
                      ? `<img src="${imgSrc(d.photoUrl)}" alt="${d.name}" style="width:180px;height:180px;object-fit:cover;border-radius:8px;border:1px solid #ddd;display:block;margin:0 auto 12px;" />`
                      : '';
                    void printDishHtml(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${d.name}</title>
<style>body{font-family:sans-serif;margin:24px;color:#222}h2{margin:0 0 10px}table{width:100%;border-collapse:collapse;font-size:13px}td{padding:6px 8px;border:1px solid #ddd}td:first-child{font-weight:600;background:#f5f5f5;width:220px}.section{margin:14px 0 6px;font-weight:700;font-size:14px}@media print{button{display:none}}</style>
</head><body>
<button onclick="window.print()" style="margin-bottom:14px;padding:6px 16px;font-size:13px;cursor:pointer">🖨️ Друк</button>
${photoHtml}
<h2>${d.name}</h2>
<table>
<tr><td>Категорія</td><td>${d.categoryName ?? '—'}</td></tr>
<tr><td>Час приготування</td><td>${formatDurationLabel(d.cookingTime)}</td></tr>
<tr><td>Час на видачу</td><td>${formatDurationLabel(d.serveTime)}</td></tr>
<tr><td>Алергени</td><td>${allergens}</td></tr>
${d.description ? `<tr><td>Опис</td><td>${d.description}</td></tr>` : ''}
</table>
<div class="section">Необхідна кількість порцій: ${detailsPortions || '—'}</div>
<table>
<tr><td>Потрібний час приготування</td><td>${formatDurationLabel(detailsRequiredCooking)}</td></tr>
<tr><td>Потрібний час на видачу</td><td>${formatDurationLabel(detailsRequiredServe)}</td></tr>
<tr><td>Разом</td><td>${formatDurationLabel(detailsRequiredCooking + detailsRequiredServe)}</td></tr>
</table>
${detailsWorkStart && detailsWorkEnd ? `<div class="section">Калькулятор часу (за фактом роботи)</div><table>
<tr><td>Початок / Кінець</td><td>${detailsWorkStart} — ${detailsWorkEnd}</td></tr>
<tr><td>Виготовлено порцій</td><td>${detailsWorkPortions || '—'}</td></tr>
<tr><td>Тривалість роботи</td><td>${formatDurationLabel(detailsWorkDurationSeconds)}</td></tr>
<tr><td>На 1 порцію</td><td>${detailsWorkDurationSeconds > 0 && detailsWorkPortionsValue > 0 ? formatDurationLabel(detailsWorkSecondsPerPortion) : '—'}</td></tr>
</table>` : ''}
</body></html>`, d.photoUrl, false);
                  }}
                  style={{ fontSize: '13px', padding: '4px 12px', borderRadius: '6px', border: '1px solid var(--crm-border)', background: 'var(--crm-surface-alt)', cursor: 'pointer', color: 'var(--crm-text)' }}
                >🖨️ Друк</button>
                <button type="button" onClick={() => setDetailsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1, color: 'var(--crm-text)' }}>×</button>
              </div>
            </div>
            <div className="dish-details-meta dish-details-modal__summary" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px', color: 'var(--crm-text)' }}>
              <div>Категорія: <strong>{detailsDish.categoryName ?? '—'}</strong></div>
              <div>Час приготування: <strong>{formatDurationLabel(detailsDish.cookingTime)}</strong></div>
              <div>Час на видачу: <strong>{formatDurationLabel(detailsDish.serveTime)}</strong></div>
              <div>
                Алергени:{' '}
                <strong>
                  {getDishComputedAllergens(detailsDish).length > 0
                    ? getDishComputedAllergens(detailsDish).map((a) => `${getAllergenIcon(a.name)} ${a.name}`).join(', ')
                    : '—'}
                </strong>
              </div>
            </div>
            <RecipeCalculationCard
              title="Калькуляційна карта страви"
              rows={detailsDish.ingredients.map((ingredient) => ({
                id: ingredient.id,
                name: ingredient.productName || ingredient.semiFinishedProductName || '—',
                grossWeight: ingredient.bruttoWeight,
                netWeight: ingredient.nettoWeight,
                output: ingredient.output,
                piecesPerPortion: ingredient.piecesPerPortion,
              }))}
            />
            <div className="dish-details-portion-panel dish-details-modal__recipe-overview" style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '12px', background: 'var(--crm-surface-alt)', display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
              {/* Ліва колонка: фото + поле вводу */}
              <div className="dish-details-portion-controls dish-details-modal__photo-panel" style={{ display: 'flex', flexDirection: 'column', gap: '10px', flexShrink: 0, width: '180px' }}>
                {detailsDish.photoUrl ? (
                  <AuthorizedApiImage
                    src={detailsDish.photoUrl}
                    alt={detailsDish.name}
                    style={{ width: '180px', height: '180px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--crm-border)' }}
                  />
                ) : (
                  <div className="dish-details-modal__photo-placeholder" style={{ width: '180px', height: '140px', borderRadius: '8px', border: '1px dashed var(--crm-border)', background: 'var(--crm-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--crm-text-muted)', fontSize: '12px', textAlign: 'center' }}>
                    Фото відсутнє
                  </div>
                )}
                <label style={{ ...lbl, margin: 0 }}>
                  Людей:
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={inp}
                    value={detailsPortions}
                    onChange={(e) => setDetailsPortions(e.target.value)}
                    placeholder="0"
                  />
                </label>
              </div>
              {/* Права колонка: 3 блоки з часом */}
              <div className="dish-details-time-results dish-details-modal__time-panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {[
                  ['Потрібний час приготування', formatDurationLabel(detailsRequiredCooking)],
                  ['Потрібний час на видачу', formatDurationLabel(detailsRequiredServe)],
                  ['Разом', formatDurationLabel(detailsRequiredCooking + detailsRequiredServe)],
                ].map(([label, value]) => (
                  <div key={label} style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', background: 'var(--crm-surface)', padding: '10px 12px', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '4px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)', fontWeight: 600 }}>{label}</span>
                    <strong style={{ fontSize: '15px', color: 'var(--crm-text)', fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '10px 12px', background: 'var(--crm-surface-alt)', display: 'grid', gap: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--crm-text)' }}>
                Калькулятор часу на 1 порцію (за фактом роботи)
              </div>
              <div className="crm-mobile-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))', gap: '10px' }}>
                <label style={{ ...lbl, margin: 0 }}>
                  Початок роботи
                  <input
                    type="time"
                    style={inp}
                    value={detailsWorkStart}
                    onChange={(e) => setDetailsWorkStart(e.target.value)}
                  />
                </label>
                <label style={{ ...lbl, margin: 0 }}>
                  Кінець роботи
                  <input
                    type="time"
                    style={inp}
                    value={detailsWorkEnd}
                    onChange={(e) => setDetailsWorkEnd(e.target.value)}
                  />
                </label>
                <label style={{ ...lbl, margin: 0 }}>
                  Виготовлено порцій
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    style={inp}
                    value={detailsWorkPortions}
                    onChange={(e) => setDetailsWorkPortions(e.target.value)}
                    placeholder="0"
                  />
                </label>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '10px' }}>
                {[
                  ['Тривалість роботи', formatDurationLabel(detailsWorkDurationSeconds)],
                  ['На 1 порцію', detailsWorkDurationSeconds > 0 && detailsWorkPortionsValue > 0 ? formatDurationLabel(detailsWorkSecondsPerPortion) : '—'],
                ].map(([label, value]) => (
                  <div key={label} style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', background: 'var(--crm-surface)', padding: '10px 12px', minHeight: '68px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)', fontWeight: 600 }}>{label}</span>
                    <strong style={{ fontSize: '15px', color: 'var(--crm-text)', lineHeight: 1.25, fontVariantNumeric: 'tabular-nums' }}>{value}</strong>
                  </div>
                ))}
              </div>
            </div>
            {detailsDish.description && (
              <div style={{ fontSize: '13px', color: 'var(--crm-text)', lineHeight: 1.5 }}>
                Опис: <strong>{detailsDish.description}</strong>
              </div>
            )}
          </div>
        </div>
      )}

      {printManageModalOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="dish-print-manage-modal" style={{ ...panelStyle, width: '100%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Керування виборами для друку страв</h3>
              <button type="button" onClick={() => setPrintManageModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div className="dish-print-column-sections" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Колонки: звичайний друк</div>
                <div className="dish-print-column-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {printColumnOptions.map((column) => (
                    <label key={`dish-col-${column.key}`} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: 'var(--crm-text)' }}>
                      <input type="checkbox" checked={dishPrintColumns[column.key]} onChange={() => toggleDishPrintColumn(column.key)} />
                      {column.label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Колонки: друк для бухгалтера</div>
                <div className="dish-print-column-options" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  {printColumnOptions.map((column) => (
                    <label key={`acc-col-${column.key}`} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: 'var(--crm-text)' }}>
                      <input type="checkbox" checked={accountingPrintColumns[column.key]} onChange={() => toggleAccountingPrintColumn(column.key)} />
                      {column.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="dish-print-selection-layout" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Поточний вибір</div>
                <div style={{ fontSize: '13px', color: 'var(--crm-text)', marginBottom: '10px' }}>
                  Вибрано вручну: <strong>{selectedIds.length}</strong>, категорій: <strong>{selectedCategoryCount}</strong>, до друку: <strong>{resolvedPrintPreviewIds.length}</strong>
                </div>
                <label style={{ ...lbl, marginBottom: '8px' }}>
                  Назва набору
                  <input style={inp} value={printSelectionName} onChange={(e) => setPrintSelectionName(e.target.value)} placeholder="Наприклад: Меню вихідного дня" />
                </label>
                <button type="button" onClick={saveCurrentPrintSelection} style={{ ...buttonStyle('primary'), cursor: 'pointer', fontWeight: 600 }}>
                  Зберегти поточний набір
                </button>
              </div>

              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Категорії для друку</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '220px', overflow: 'auto' }}>
                  {categories.map((category) => (
                    <label key={category.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)' }}>
                      <input type="checkbox" checked={printCategoryIds.includes(category.id)} onChange={() => togglePrintCategory(category.id)} />
                      {category.name}
                    </label>
                  ))}
                  {categories.length === 0 && <span style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Категорії відсутні</span>}
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button type="button" onClick={() => handlePrintSelectedBatch(selectedIds, printCategoryIds, false)} style={{ ...buttonStyle('accent'), cursor: 'pointer', fontWeight: 600 }}>
                🖨 Друк поточного вибору
              </button>
              <button type="button" onClick={() => handlePrintSelectedBatch(selectedIds, printCategoryIds, true)} style={{ ...buttonStyle('success'), cursor: 'pointer', fontWeight: 600 }}>
                Бухгалтерський друк
              </button>
            </div>

            <div style={{ borderTop: '1px solid var(--crm-border)', paddingTop: '12px' }}>
              <div style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--crm-text)' }}>Збережені набори</div>
              {savedPrintSelections.length === 0 ? (
                <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Ще немає збережених наборів.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--crm-surface-alt)' }}>
                      <th style={{ ...td, fontWeight: 700 }}>Назва</th>
                      <th style={{ ...td, fontWeight: 700 }}>Вручну</th>
                      <th style={{ ...td, fontWeight: 700 }}>Категорії</th>
                      <th style={{ ...td, fontWeight: 700 }}>Дії</th>
                    </tr>
                  </thead>
                  <tbody>
                    {savedPrintSelections.map((selection) => (
                      <tr key={selection.id} style={{ borderTop: '1px solid var(--crm-table-row-border)' }}>
                        <td style={{ ...td, fontWeight: 500 }}>{selection.name}</td>
                        <td style={td}>{selection.selectedIds.length}</td>
                        <td style={td}>{selection.categoryIds.length}</td>
                        <td style={td}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button type="button" onClick={() => applySavedSelection(selection)} style={{ ...buttonStyle('primary'), padding: '5px 9px', cursor: 'pointer' }}>Застосувати</button>
                            <button type="button" onClick={() => handlePrintSelectedBatch(selection.selectedIds, selection.categoryIds, false)} style={{ ...buttonStyle('accent'), padding: '5px 9px', cursor: 'pointer' }}>Друк</button>
                            <button type="button" onClick={() => handlePrintSelectedBatch(selection.selectedIds, selection.categoryIds, true)} style={{ ...buttonStyle('success'), padding: '5px 9px', cursor: 'pointer' }}>Бух.</button>
                            <button type="button" onClick={() => removeSavedSelection(selection.id)} style={{ ...buttonStyle('danger'), padding: '5px 9px', cursor: 'pointer' }}>Видалити</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {replaceIngredientsModalOpen && (
        <div className="dish-replace-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2080, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="dish-replace-modal" style={{ ...panelStyle, width: '100%', maxWidth: '900px', maxHeight: '85vh', overflow: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="dish-replace-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Масова заміна інгредієнтів</h3>
              <button type="button" onClick={() => setReplaceIngredientsModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px' }}>
              <div style={{ fontWeight: 600, color: 'var(--crm-text)', marginBottom: '8px' }}>
                Обрані страви ({selectedDishesForReplace.length})
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '140px', overflowY: 'auto' }}>
                {selectedDishesForReplace.map((dish) => (
                  <span key={dish.id} style={{ fontSize: '12px', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', borderRadius: '999px', padding: '4px 10px' }}>
                    {dish.name}
                  </span>
                ))}
              </div>
            </div>

            <div className="dish-replace-fields" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <label style={lbl}>
                Інгредієнт (що замінюємо)
                <SearchSelect
                  items={commonIngredientsForReplace}
                  value={replaceFromIngredientId}
                  onChange={setReplaceFromIngredientId}
                  placeholder="🔍 Оберіть інгредієнт..."
                />
              </label>

              <label style={lbl}>
                Новий інгредієнт (на що замінюємо)
                <SearchSelect
                  items={allIngredients}
                  value={replaceToIngredientId}
                  onChange={setReplaceToIngredientId}
                  placeholder="🔍 Пошук інгредієнта для заміни..."
                />
              </label>
            </div>

            {commonIngredientsForReplace.length === 0 && (
              <div style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', padding: '10px', borderRadius: 'var(--crm-radius-sm)', fontSize: '13px' }}>
                У вибраних стравах немає інгредієнтів для заміни.
              </div>
            )}

            <div className="dish-replace-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setReplaceIngredientsModalOpen(false)}
                disabled={replacingIngredients}
                style={{ ...buttonStyle('muted'), cursor: replacingIngredients ? 'not-allowed' : 'pointer' }}
              >
                Скасувати
              </button>
              <button
                type="button"
                onClick={handleReplaceIngredients}
                disabled={replacingIngredients || commonIngredientsForReplace.length === 0}
                style={{ ...buttonStyle((replacingIngredients || commonIngredientsForReplace.length === 0) ? 'muted' : 'accent'), cursor: (replacingIngredients || commonIngredientsForReplace.length === 0) ? 'not-allowed' : 'pointer', fontWeight: 600 }}
              >
                {replacingIngredients ? 'Збереження...' : 'Зберегти заміну'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteByCategoryModalOpen && (
        <div className="dish-delete-category-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div className="dish-delete-category-modal" style={{ ...panelStyle, width: '100%', maxWidth: '620px', padding: '18px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="dish-delete-category-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, color: 'var(--crm-text)' }}>Видалення страв за категоріями</h3>
              <button type="button" onClick={() => setDeleteByCategoryModalOpen(false)} style={{ background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ fontSize: '13px', color: 'var(--crm-text)' }}>
              Обрано категорій: <strong>{deleteCategoryIds.length}</strong>, страв до видалення: <strong>{getDishIdsByCategories(deleteCategoryIds).length}</strong>
            </div>

            <div className="dish-delete-category-options" style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '10px', maxHeight: '320px', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {categories.map((category) => {
                const count = items.filter((dish) => dish.categoryId === category.id).length;
                return (
                  <label key={category.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--crm-text)', fontSize: '13px' }}>
                    <input type="checkbox" checked={deleteCategoryIds.includes(category.id)} onChange={() => toggleDeleteCategory(category.id)} />
                    <span style={{ flex: 1 }}>{category.name}</span>
                    <span style={{ color: 'var(--crm-text-muted)' }}>{count}</span>
                  </label>
                );
              })}
              {categories.length === 0 && <div style={{ fontSize: '13px', color: 'var(--crm-text-muted)' }}>Категорії відсутні</div>}
            </div>

            <div className="dish-delete-category-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" onClick={() => setDeleteByCategoryModalOpen(false)} style={{ ...buttonStyle('muted'), cursor: 'pointer' }}>
                Скасувати
              </button>
              <button type="button" onClick={handleDeleteByCategories} style={{ ...buttonStyle('danger'), cursor: 'pointer', fontWeight: 600 }}>
                Видалити страви категорій
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="recipe-editor-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '24px 16px' }}>
          <div className="recipe-editor-modal" style={{ ...panelStyle, width: '100%', maxWidth: '1100px', marginBottom: '24px' }}>

            <div className="recipe-editor-header" style={{ background: 'var(--crm-accent)', color: 'var(--crm-accent-contrast)', padding: '16px 20px', borderRadius: 'var(--crm-radius-lg) var(--crm-radius-lg) 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '16px' }}>{editingId ? `Редагування: ${form.name}` : 'Нова страва'}</h3>
              <button onClick={closeModal} style={{ background: 'none', border: 'none', color: 'var(--crm-accent-contrast)', fontSize: '22px', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div className="recipe-editor-content" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Photo + basic fields */}
              <div className="recipe-editor-intro" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start' }}>

                {/* Photo block */}
                <div className="recipe-editor-photo" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                  <div
                    className="recipe-editor-photo-preview"
                    style={{ width: '260px', height: '260px', borderRadius: 'var(--crm-radius-lg)', overflow: 'hidden', border: `2px ${photoDisplay ? 'solid var(--crm-accent)' : 'dashed var(--crm-border)'}`, background: 'var(--crm-surface-alt)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative' }}
                    onClick={() => photoInputRef.current?.click()}
                  >
                    {photoDisplay
                      ? <AuthorizedApiImage src={form.photoBlob || form.photoUrl} alt="Фото" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ textAlign: 'center', color: 'var(--crm-text-muted)', fontSize: '13px', padding: '10px' }}>
                          <div style={{ fontSize: '40px', marginBottom: '6px' }}>📷</div>
                          <div>Натисніть для<br />завантаження фото</div>
                        </div>}
                    {uploadingPhoto && (
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(17, 24, 32, .75)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', color: 'var(--crm-primary-contrast)' }}>
                        Завантаження...
                      </div>
                    )}
                  </div>
                  <input ref={photoInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoSelect} />
                  {photoDisplay && (
                    <button type="button" onClick={() => { setForm(f => ({ ...f, photoUrl: '', photoBlob: undefined })); pendingPhotoRef.current = null; }}
                      style={{ fontSize: '12px', background: 'none', border: '1px solid var(--crm-danger)', borderRadius: 'var(--crm-radius-sm)', color: 'var(--crm-danger)', padding: '3px 10px', cursor: 'pointer' }}>
                      🗑 Видалити
                    </button>
                  )}
                  <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)', textAlign: 'center' }}>JPG, PNG, WEBP, GIF, BMP, TIFF (до 30MB)</span>
                </div>

                {/* Basic fields */}
                <div className="recipe-editor-basic-fields" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{ border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '12px', background: 'var(--crm-surface-alt)' }}>
                    <div className="recipe-editor-basic-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1.6fr 1.6fr', gap: '12px' }}>
                      <label style={{ ...lbl, gridColumn: 'span 2' }}>
                        Назва *
                        <input style={inp} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Назва страви" />
                      </label>
                      <B52Autocomplete
                        b52Code={form.b52Code}
                        b52Name={form.b52Name}
                        onChange={(code, name) => setForm({ ...form, b52Code: code, b52Name: name })}
                        labelStyle={lbl}
                        inputStyle={inp}
                      />
                      <label style={lbl}>
                        Категорія
                        <SearchSelect
                          items={categorySearchItems}
                          value={form.categoryId}
                          onChange={(id) => setForm({ ...form, categoryId: id })}
                          placeholder="🔍 Пошук категорії..."
                        />
                      </label>
                      <label style={lbl}>
                        <span style={{ whiteSpace: 'nowrap' }}>Час приготування (сек або хв,сек)</span>
                        <input style={inp} value={form.cookingTime} onChange={e => setForm({ ...form, cookingTime: e.target.value })} placeholder="напр. 95 або 1,35" />
                      </label>
                      <label style={lbl}>
                        <span style={{ whiteSpace: 'nowrap' }}>Час на видачу (сек або хв,сек)</span>
                        <input style={inp} value={form.serveTime} onChange={e => setForm({ ...form, serveTime: e.target.value })} placeholder="напр. 45 або 0,45" />
                      </label>
                      <label style={lbl}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span>Кількість порцій</span>
                          <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)', fontWeight: 500 }}>Показується в назві страви</span>
                        </span>
                        <input type="number" min="0" step="1" name="servingsCount" style={inp} value={form.servingsCount} onChange={e => setForm({ ...form, servingsCount: e.target.value })} placeholder="напр. 10" />
                      </label>
                      <label style={lbl}>
                        Одиниця виміру
                        <select style={inp} value={form.weightType} onChange={e => setForm({ ...form, weightType: e.target.value })}>
                          {WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                        </select>
                      </label>
                    </div>
                  </div>
                  <label style={lbl}>
                    Опис
                    <textarea style={{ ...inp, resize: 'vertical', minHeight: '70px' }} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Короткий опис страви" />
                  </label>
                </div>
              </div>

              {/* Technology */}
              <label style={lbl}>
                Технологія приготування
                <textarea
                  style={{ ...inp, resize: 'vertical', minHeight: '120px', fontFamily: 'inherit', lineHeight: '1.6' }}
                  value={form.technologyDescription}
                  onChange={e => setForm({ ...form, technologyDescription: e.target.value })}
                  placeholder="Детальний опис технологічного процесу: підготовка інгредієнтів, послідовність дій, температурний режим, час кожного етапу..."
                />
              </label>
              <RecipePreparationStepsEditor
                value={form.preparationSteps}
                onChange={preparationSteps => setForm(current => ({ ...current, preparationSteps }))}
                onUploadPhoto={uploadPreparationStepPhoto}
                canUploadPhoto={editingId != null}
              />

              {/* Ingredients */}
              <div style={{ ...panelStyle, overflow: 'hidden' }}>
                <div className="recipe-editor-section-header" style={{ background: 'var(--crm-primary)', color: 'var(--crm-primary-contrast)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong style={{ fontSize: '14px' }}>Склад страви</strong>
                  <div className="recipe-editor-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <button type="button" onClick={() => setIngredientColumns((prev) => ({ ...prev, notes: !prev.notes }))} style={{ ...buttonStyle(ingredientColumns.notes ? 'accent' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.notes ? 'Сховати нотатки' : 'Показати нотатки'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((prev) => ({ ...prev, price: !prev.price }))} style={{ ...buttonStyle(ingredientColumns.price ? 'success' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.price ? 'Сховати ціну' : 'Показати ціну'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((prev) => ({ ...prev, cost: !prev.cost }))} style={{ ...buttonStyle(ingredientColumns.cost ? 'primary' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.cost ? 'Сховати вартість' : 'Показати вартість'}
                    </button>
                    <button type="button" onClick={() => setIngredientColumns((prev) => ({ ...prev, processing: !prev.processing }))} style={{ ...buttonStyle(ingredientColumns.processing ? 'danger' : 'muted'), padding: '5px 10px', cursor: 'pointer', fontSize: '12px' }}>
                      {ingredientColumns.processing ? 'Сховати обробку' : 'Показати обробку'}
                    </button>
                    <button type="button" onClick={() => setShowQuickProduct(v => !v)} style={{ ...buttonStyle('success'), padding: '5px 12px', cursor: 'pointer', fontSize: '13px' }}>
                      {showQuickProduct ? 'Сховати продукт' : '+ Новий продукт'}
                    </button>
                    <button type="button" onClick={openSemiFromRows} disabled={rows.every((r) => !r.refId)} title="Створити напівфабрикат з продуктів, уже доданих у склад страви" style={{ ...buttonStyle(rows.every((r) => !r.refId) ? 'muted' : 'warning'), padding: '5px 12px', cursor: rows.every((r) => !r.refId) ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: rows.every((r) => !r.refId) ? 0.6 : 1 }}>
                      {showSemiFromRows ? 'Сховати Н/Ф' : '+ Н/Ф зі складу'}
                    </button>
                    <button type="button" onClick={addRow} style={{ ...buttonStyle('primary'), padding: '5px 16px', cursor: 'pointer', fontSize: '13px' }}>+ Рядок</button>
                  </div>
                </div>
                {showQuickProduct && (
                  <div className="recipe-editor-quick-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '8px', padding: '10px 16px', background: 'var(--crm-success-bg)', borderBottom: '1px solid var(--crm-border)' }}>
                    <input style={{ ...inp, padding: '6px 8px' }} value={quickProduct.name} onChange={(e) => setQuickProduct({ ...quickProduct, name: e.target.value })} placeholder="Назва нового продукту" />
                    <input type="number" min="0" step="0.01" style={{ ...inp, padding: '6px 8px' }} value={quickProduct.price} onChange={(e) => setQuickProduct({ ...quickProduct, price: e.target.value })} placeholder="Ціна" />
                    <select style={{ ...inp, padding: '6px 8px' }} value={quickProduct.weightType} onChange={(e) => setQuickProduct({ ...quickProduct, weightType: e.target.value })}>
                      {WEIGHT_TYPES.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                    <select style={{ ...inp, padding: '6px 8px' }} value={quickProduct.categoryId} onChange={(e) => setQuickProduct({ ...quickProduct, categoryId: e.target.value })}>
                      <option value="">— Категорія —</option>
                      {productCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    <button type="button" disabled={creatingProduct} onClick={handleQuickCreateProduct} style={{ ...buttonStyle(creatingProduct ? 'muted' : 'success'), padding: '0 14px', cursor: creatingProduct ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                      {creatingProduct ? 'Створення...' : 'Додати'}
                    </button>
                  </div>
                )}
                {showSemiFromRows && (
                  <div style={{ padding: '10px 16px', background: 'var(--crm-warning-bg)', borderBottom: '1px solid var(--crm-border)', display: 'grid', gap: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--crm-warning)' }}>Позначте рядки складу, з яких буде створено Н/Ф. Ваги (брутто/нетто/вихід) та операції перенесуться у його склад.</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {rows.filter((r) => r.refId).map((r) => {
                        const label = allIngredients.find((i) => i.id === encodeIngredientId(r))?.name ?? '—';
                        const checked = semiFromRows.selectedKeys.includes(r._key);
                        return (
                          <label key={r._key} style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '5px', border: `1px solid ${checked ? 'var(--crm-warning)' : 'var(--crm-border)'}`, background: checked ? 'var(--crm-surface)' : 'var(--crm-surface-alt)', color: 'var(--crm-text)', fontSize: '12px', cursor: 'pointer' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSemiRow(r._key)} />
                            {label} <span style={{ color: 'var(--crm-text-muted)' }}>({r.bruttoWeight || 0} / {r.output || 0})</span>
                          </label>
                        );
                      })}
                    </div>
                    <div className="recipe-editor-quick-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto auto', gap: '8px', alignItems: 'center' }}>
                      <input style={{ ...inp, padding: '6px 8px' }} value={semiFromRows.name} onChange={(e) => setSemiFromRows({ ...semiFromRows, name: e.target.value })} placeholder="Назва нового Н/Ф" />
                      <select style={{ ...inp, padding: '6px 8px' }} value={semiFromRows.categoryId} onChange={(e) => setSemiFromRows({ ...semiFromRows, categoryId: e.target.value })}>
                        <option value="">— Категорія Н/Ф —</option>
                        {semiFinishedCategories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                      <input type="number" min="0" step="0.001" title="Фактичний вихід Н/Ф (за замовчуванням — сума виходу обраних рядків)" style={{ ...inp, padding: '6px 8px' }} value={semiFromRows.output} onChange={(e) => setSemiFromRows({ ...semiFromRows, output: e.target.value })} placeholder="Вихід Н/Ф" />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={semiFromRows.replaceRows} onChange={(e) => setSemiFromRows({ ...semiFromRows, replaceRows: e.target.checked })} />
                        Замінити рядки на Н/Ф
                      </label>
                      <button type="button" disabled={creatingSemi} onClick={handleCreateSemiFromRows} style={{ ...buttonStyle(creatingSemi ? 'muted' : 'warning'), padding: '7px 14px', cursor: creatingSemi ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600 }}>
                        {creatingSemi ? 'Створення...' : 'Створити Н/Ф'}
                      </button>
                    </div>
                  </div>
                )}
                <div style={{ minHeight: '180px' }}>
                  <p style={{ margin: 0, padding: '10px 16px', fontSize: 12, background: 'var(--crm-info-bg)', color: 'var(--crm-info)' }}>
                    Нетто — реальна вага. Кількість шматочків за потреби задайте окремо в полі «Шт./порц.»:
                    наприклад, 0,15 кг та 3 шт. Для 20 порцій бланк покаже 3 кг та 60 шт.
                    Штуки не впливають на списання й собівартість. Старі записи шматочків у Нетто виправте вручну.
                  </p>
                  <table className="recipe-ingredient-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr>
                      {ingredientTableColumns.map((column) => (
                         <th key={column.key} style={{ ...th, padding: '10px', ...(column.width ? { width: column.width } : {}) }}>{column.title}</th>
                      ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map(row => {
                        const b = parseFloat(row.bruttoWeight) || 0;
                        const sfObj = row.type === 'semi' ? semiFinished.find(s => s.id === parseInt(row.refId)) : undefined;
                        const ingredientWeightType = row.type === 'product'
                          ? products.find(product => product.id === parseInt(row.refId))?.weightType
                          : sfObj?.weightType;
                        const ingredientUnit = WEIGHT_TYPES.find(weight => weight.value === ingredientWeightType)?.label;
                        const excluded = sfObj?.isAuxiliaryRecipe || sfObj?.excludeFromDishWeightCalc || false;
                        const up = (row.type === 'product'
                          ? (products.find(p => p.id === parseInt(row.refId))?.price ?? 0)
                          : getSFPrice(parseInt(row.refId)));
                        const rc = up * b;
                        const selOp = preparationOperations.find(o => String(o.id) === row.preparationOperationId);
                        return (
                          <tr key={row._key} style={{ borderBottom: '1px solid var(--crm-table-row-border)' }}>
                            <td data-label="Інгредієнт" style={{ ...td, padding: '8px' }}>
                              <ItemQuantityField
                                items={allIngredients}
                                value={encodeIngredientId(row)}
                                onChange={id => {
                                  const decoded = decodeIngredientId(id);
                                  updateRow(row._key, { type: decoded.type, refId: decoded.refId });
                                }}
                                quantity={row.bruttoWeight}
                                onQuantityChange={bruttoWeight => updateRow(row._key, { bruttoWeight })}
                                label="Інгредієнт"
                                quantityLabel="Брутто"
                                unit={ingredientUnit}
                                min={0}
                                step={0.001}
                                quantityRequired={false}
                                placeholder="🔍 Продукт або напівфабрикат..."
                              />
                              {excluded && <span style={{ fontSize: '11px', color: 'var(--crm-warning)', marginLeft: '4px' }}>вага не рахується</span>}
                            </td>
                            {(['nettoWeight'] as const).map(fld => (
                              <td key={fld} data-label="Нетто" style={{ ...td, padding: '8px' }}>
                                <input type="number" min="0" step="0.001" style={{ ...inp, padding: '5px', fontSize: '13px' }}
                                  value={row[fld]} onChange={e => updateRow(row._key, { [fld]: e.target.value })} placeholder="0" />
                              </td>
                            ))}
                            <td data-label="Шт./порц." style={{ ...td, padding: '8px' }}>
                              <input type="number" min="0.001" step="0.001" aria-label="Штук на порцію" title="Кількість шматочків на одну порцію, не вага. Не впливає на списання чи вартість."
                                style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.piecesPerPortion}
                                onChange={e => updateRow(row._key, { piecesPerPortion: e.target.value })} placeholder="—" />
                            </td>
                            <td data-label="Вихід" style={{ ...td, padding: '8px' }}>
                              <input type="number" min="0" step="0.001" style={{ ...inp, padding: '5px', fontSize: '13px' }}
                                value={row.output} onChange={e => updateRow(row._key, { output: e.target.value })} placeholder="0" />
                            </td>
                            {ingredientColumns.price && <td data-label="Ціна/од" style={{ ...td, color: 'var(--crm-text-muted)', padding: '8px' }}>{up > 0 ? `${up.toFixed(2)} грн` : '—'}</td>}
                            {ingredientColumns.cost && <td data-label="Вартість" style={{ ...td, fontWeight: 600, color: rc > 0 ? 'var(--crm-success)' : 'var(--crm-text-muted)', padding: '8px' }}>{rc > 0 ? `${rc.toFixed(2)} грн` : '—'}</td>}
                            {ingredientColumns.notes && <td data-label="Нотатки картки" style={{ ...td, padding: '8px' }}>
                              <input type="text" style={{ ...inp, padding: '5px', fontSize: '13px' }} value={row.notes} onChange={e => updateRow(row._key, { notes: e.target.value })} placeholder="Нотатка..." />
                            </td>}
                            {ingredientColumns.processing && <td data-label="Обробка" style={{ ...td, padding: '8px' }}>
                              <select
                                style={{ ...inp, padding: '5px', fontSize: '12px' }}
                                value={row.processingMethodId}
                                onChange={e => {
                                  const method = processingMethods.find((item) => String(item.id) === e.target.value);
                                  updateRow(row._key, {
                                    processingMethodId: e.target.value,
                                    processingTemperatureC: method?.defaultTemperatureC != null ? String(method.defaultTemperatureC) : row.processingTemperatureC,
                                  });
                                }}
                              >
                                <option value="">— без обробки —</option>
                                {processingMethods.map(method => (
                                  <option key={method.id} value={String(method.id)}>{method.name}</option>
                                ))}
                              </select>
                            </td>}
                            {ingredientColumns.processing && <td data-label="Температура °C" style={{ ...td, padding: '8px' }}>
                              <input
                                type="number"
                                step="1"
                                style={{ ...inp, padding: '5px', fontSize: '13px' }}
                                value={row.processingTemperatureC}
                                onChange={e => updateRow(row._key, { processingTemperatureC: e.target.value })}
                                placeholder="°C"
                              />
                            </td>}
                            <td data-label="Операція" style={{ ...td, padding: '8px' }}>
                              <select
                                style={{ ...inp, padding: '5px', fontSize: '12px', borderLeft: selOp ? `3px solid ${selOp.color}` : undefined }}
                                value={row.preparationOperationId}
                                onChange={e => updateRow(row._key, { preparationOperationId: e.target.value })}
                              >
                                <option value="">— жодна —</option>
                                {preparationOperations.map(op => (
                                  <option key={op.id} value={String(op.id)}>{op.name}</option>
                                ))}
                              </select>
                            </td>
                            <td data-label="Інфо по операції" style={{ ...td, padding: '8px' }}>
                              <input
                                type="text"
                                style={{ ...inp, padding: '5px', fontSize: '13px' }}
                                value={row.operationNotes}
                                onChange={e => updateRow(row._key, { operationNotes: e.target.value })}
                                placeholder="Опис операції..."
                              />
                            </td>
                            <td data-label="Видалити" style={{ ...td, padding: '8px' }}>
                              <button type="button" onClick={() => removeRow(row._key)}
                                style={{ ...buttonStyle('danger'), minWidth: '44px', minHeight: '44px', cursor: 'pointer', fontSize: '18px', lineHeight: 1 }}>×</button>
                            </td>
                          </tr>
                        );
                      })}
                      {rows.length === 0 && (
                        <tr><td colSpan={ingredientTableColumns.length} style={{ ...td, padding: '20px', textAlign: 'center', color: 'var(--crm-text-muted)' }}>Натисніть "+ Рядок" щоб додати інгредієнт</td></tr>
                      )}
                    </tbody>
                    {rows.length > 0 && (
                      <tfoot>
                        <tr style={{ background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', fontWeight: 700 }}>
                          <td colSpan={ingredientTableColumns.findIndex((column) => column.key === 'netto')} style={{ ...td, textAlign: 'right', padding: '10px' }}>Σ Брутто: {totalBrutto.toFixed(3)}</td>
                          <td style={{ ...td, color: 'var(--crm-info)', padding: '10px' }}>{totalNetto.toFixed(3)}</td>
                          <td style={{ padding: '10px' }}></td>
                          <td style={{ ...td, color: 'var(--crm-info)', padding: '10px' }}>{totalOutput.toFixed(3)}</td>
                          {ingredientColumns.price && <td style={{ padding: '10px' }}></td>}
                          {ingredientColumns.cost && <td style={{ ...td, color: 'var(--crm-success)', fontSize: '14px', padding: '10px' }}>{cost.toFixed(2)} грн</td>}
                          {ingredientColumns.notes && <td style={{ padding: '10px' }}></td>}
                          {ingredientColumns.processing && <td style={{ padding: '10px' }}></td>}
                          {ingredientColumns.processing && <td style={{ padding: '10px' }}></td>}
                          <td style={{ padding: '10px' }}></td>
                          <td style={{ padding: '10px' }}></td>
                          <td style={{ padding: '10px' }}></td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
                <div className="dish-recipe-scale-grid" style={{ padding: '10px 16px', background: 'var(--crm-info-bg)', borderTop: '1px solid var(--crm-border)', borderBottom: '1px solid var(--crm-border)', display: 'grid', gridTemplateColumns: '190px 180px auto', gap: '10px', alignItems: 'end' }}>
                  <label style={{ ...lbl, margin: 0 }}>
                    База перерахунку
                    <select style={inp} value={scaleBasis} onChange={(e) => setScaleBasis(e.target.value as 'brutto' | 'netto' | 'output')}>
                      <option value="brutto">Сума Брутто</option>
                      <option value="netto">Сума Нетто</option>
                      <option value="output">Сума Вихід</option>
                    </select>
                  </label>
                  <label style={{ ...lbl, margin: 0 }}>
                    Цільова вага
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      style={inp}
                      value={scaleTargetWeight}
                      onChange={(e) => setScaleTargetWeight(e.target.value)}
                      placeholder="Напр. 1.500"
                    />
                  </label>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontSize: '12px', color: 'var(--crm-text-muted)' }}>
                      Поточна сума: <strong>{
                        scaleBasis === 'brutto'
                          ? totalBrutto.toFixed(3)
                          : (scaleBasis === 'netto' ? totalNetto.toFixed(3) : totalOutput.toFixed(3))
                      }</strong>
                    </span>
                    <button
                      type="button"
                      onClick={handleScaleIngredients}
                      style={{ ...buttonStyle('primary'), height: '38px', padding: '0 18px', cursor: 'pointer', fontWeight: 700, fontSize: '13px', justifySelf: 'start' }}
                    >
                      Перерахувати пропорційно
                    </button>
                  </div>
                </div>
                <div style={{ padding: '10px 16px', background: 'var(--crm-surface-alt)', borderTop: '1px solid var(--crm-border)', display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px', color: 'var(--crm-text)' }}>
                  <span>Брутто: <strong>{totalBrutto.toFixed(3)}</strong></span>
                  <span>Нетто: <strong>{totalNetto.toFixed(3)}</strong></span>
                  <span>Вихід (прогноз): <strong>{totalOutput.toFixed(3)}</strong></span>
                  <span>% втрат (заг.): <strong style={{ color: 'var(--crm-warning)' }}>{totalLossPercent.toFixed(2)}%</strong></span>
                  <span>Вихід готової страви (факт): <strong>{readyDishOutput.toFixed(3)}</strong></span>
                  <span style={{ marginLeft: 'auto', color: 'var(--crm-text-muted)' }}>Собівартість: <strong style={{ color: 'var(--crm-text)' }}>{cost.toFixed(2)} грн</strong></span>
                </div>
              </div>

              <div style={{ ...panelStyle, overflow: 'hidden' }}>
                <div style={{ background: 'var(--crm-success-bg)', color: 'var(--crm-success)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                  <strong>Харчова та енергетична цінність</strong>
                  <button type="button" onClick={loadNutrition} disabled={nutritionLoading || !editingId} style={{ ...buttonStyle(nutritionLoading || !editingId ? 'muted' : 'success'), padding: '6px 12px', cursor: nutritionLoading || !editingId ? 'not-allowed' : 'pointer' }}>
                    {nutritionLoading ? 'Розрахунок...' : 'Розрахувати'}
                  </button>
                </div>
                <div style={{ padding: 14, background: 'var(--crm-surface-alt)', color: 'var(--crm-text)' }}>
                  {!editingId && <div style={{ color: 'var(--crm-text-muted)' }}>Збережіть страву, щоб отримати розрахунок за прив’язаними базовими продуктами.</div>}
                  {nutritionResult && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
                        {[
                          ['Ккал', nutritionResult.totals.energyKcal],
                          ['Білки', nutritionResult.totals.protein],
                          ['Жири', nutritionResult.totals.fat],
                          ['Вуглеводи', nutritionResult.totals.carbohydrates],
                          ['Клітковина', nutritionResult.totals.fiber],
                        ].map(([label, value]) => (
                          <div key={String(label)} style={{ ...panelStyle, boxShadow: 'none', padding: 10 }}>
                            <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>{label}</div>
                            <strong>{Number(value).toFixed(2)}</strong>
                          </div>
                        ))}
                      </div>
                      {nutritionResult.per100g && (
                        <div style={{ fontSize: 13, color: 'var(--crm-text)', marginBottom: 8 }}>
                          На 100 г: {nutritionResult.per100g.energyKcal.toFixed(1)} ккал · Б {nutritionResult.per100g.protein.toFixed(1)} · Ж {nutritionResult.per100g.fat.toFixed(1)} · В {nutritionResult.per100g.carbohydrates.toFixed(1)}
                        </div>
                      )}
                      {nutritionResult.perPortion && (
                        <div style={{ fontSize: 13, color: 'var(--crm-text)', marginBottom: 8 }}>
                          На порцію: {nutritionResult.perPortion.energyKcal.toFixed(1)} ккал · Б {nutritionResult.perPortion.protein.toFixed(1)} · Ж {nutritionResult.perPortion.fat.toFixed(1)} · В {nutritionResult.perPortion.carbohydrates.toFixed(1)}
                        </div>
                      )}
                      <div style={{ fontSize: 13, marginBottom: 8 }}>Алергени: <strong>{nutritionResult.allergens.map((a) => a.name).join(', ') || '—'}</strong></div>
                      {nutritionResult.warnings.length > 0 && <div style={{ color: 'var(--crm-warning)', fontSize: 12, marginBottom: 8 }}>{nutritionResult.warnings.join(' ')}</div>}
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead><tr><th style={th}>Інгредієнт</th><th style={th}>База</th><th style={th}>г</th><th style={th}>Обробка</th><th style={th}>ккал</th></tr></thead>
                        <tbody>{nutritionResult.ingredients.map((row, idx) => (
                          <tr key={idx}>
                            <td style={td}>{row.productName || row.semiFinishedProductName || '—'}</td>
                            <td style={td}>{row.baseFoodName || '—'}</td>
                            <td style={td}>{row.grams.toFixed(1)}</td>
                            <td style={td}>{row.processingMethodName || '—'} ({row.retentionPercent.toFixed(0)}%)</td>
                            <td style={td}>{row.totals.energyKcal.toFixed(1)}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </>
                  )}
                </div>
              </div>

              {/* Pricing block */}
              <div style={{ background: 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: '8px', padding: '16px' }}>
                <strong style={{ color: 'var(--crm-text)', fontSize: '14px' }}>💰 Ціноутворення</strong>
                <div className="dish-pricing-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>

                  <div style={lbl}>
                    Собівартість
                    <div style={{ ...inp, background: 'var(--crm-input-bg-muted)', color: 'var(--crm-text)', fontWeight: 600, cursor: 'default' }}>{cost.toFixed(2)} грн</div>
                  </div>

                  <label style={lbl}>
                    Націнка (%)
                    <input type="number" min="0" step="1" style={inp} value={form.margin} onChange={e => { const nextMargin = e.target.value; setLockedFinalPrice(null); setForm({ ...form, marginType: 'percent', margin: nextMargin }); }} onBlur={() => setForm((prev) => ({ ...prev, marginType: 'percent', margin: String(Math.max(0, Math.round(parseFloat(prev.margin) || 0))) }))} placeholder="0" />
                    <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>
                      = {`${marginAmount.toFixed(2)} грн`}
                    </span>
                  </label>

                  <label style={lbl}>
                    Ціна продажу
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={{ ...inp, background: 'var(--crm-success-bg)', color: 'var(--crm-success)', fontWeight: 700, fontSize: '18px', textAlign: 'center', border: '1px solid var(--crm-success)' }}
                      value={isEditingFinalPrice ? finalPriceInput : (Number.isFinite(finalPrice) ? finalPrice.toFixed(2) : '0.00')}
                      onFocus={() => {
                        setIsEditingFinalPrice(true);
                        setFinalPriceInput(Number.isFinite(finalPrice) ? finalPrice.toFixed(2) : '');
                      }}
                      onChange={(e) => setFinalPriceInput(e.target.value)}
                      onBlur={() => {
                        const trimmed = finalPriceInput.trim();
                        if (trimmed !== '') {
                          const parsed = parseFloat(trimmed);
                          if (Number.isFinite(parsed)) applyMarginFromFinalPrice(parsed);
                        }
                        setIsEditingFinalPrice(false);
                      }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>Введіть вручну — націнка перерахується автоматично</span>
                  </label>

                  <label style={lbl}>
                    Вихід готової страви (факт)
                    <input
                      type="number"
                      min="0"
                      step="0.001"
                      style={inp}
                      value={form.cleanOutput}
                      onChange={e => setForm({ ...form, cleanOutput: e.target.value })}
                      placeholder={`Прогноз: ${totalOutput.toFixed(3)}`}
                    />
                  </label>

                </div>
              </div>

              {error && <div style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', padding: '10px', borderRadius: 'var(--crm-radius-sm)' }}>{error}</div>}

              <div style={{ border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', padding: '12px 14px', display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--crm-text)', fontWeight: 600 }}>
                  <input type="checkbox" checked={saveArchiveSnapshot} onChange={(e) => setSaveArchiveSnapshot(e.target.checked)} />
                  Створити архівну копію при збереженні
                </label>
                {saveArchiveSnapshot && (
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--crm-text)' }}>
                    Діє з дати:
                    <input className="recipe-archive-effective-date" type="date" value={archiveEffectiveFromUtc} onChange={(e) => setArchiveEffectiveFromUtc(e.target.value)} style={{ ...inp, minHeight: '34px', width: '170px' }} />
                  </label>
                )}
              </div>

              {archiveVersions.length > 0 && (
                <div style={{ ...panelStyle, padding: '12px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: 'var(--crm-text)', marginBottom: '10px' }}>Архівні калькуляції</div>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    {archiveVersions.slice(0, 5).map((version) => {
                      const isSelected = selectedArchiveVersionId === version.id;
                      return (
                        <div key={version.id} style={{ background: isSelected ? 'var(--crm-info-bg)' : 'var(--crm-surface-alt)', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', padding: '10px 12px', display: 'grid', gap: '8px', color: 'var(--crm-text)' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                            <div>
                              <div style={{ fontWeight: 700, fontSize: '13px', color: 'var(--crm-text)' }}>v{version.versionNumber}</div>
                              <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>Дата: {formatArchiveVersionDate(version.effectiveFromUtc)}</div>
                            </div>
                            <div className="recipe-archive-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                              <button className="recipe-archive-action" type="button" onClick={() => setSelectedArchiveVersionId(version.id)} style={{ ...buttonStyle('muted'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Вибрати</button>
                              <button className="recipe-archive-action" type="button" onClick={() => restoreArchiveVersion(version)} style={{ ...buttonStyle('primary'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Відкрити</button>
                              <button className="recipe-archive-action" type="button" onClick={() => deleteArchiveVersion(version)} style={{ ...buttonStyle('danger'), padding: '6px 10px', cursor: 'pointer', fontSize: '11px' }}>Видалити</button>
                            </div>
                          </div>
                          <div style={{ fontSize: '11px', color: 'var(--crm-text-muted)' }}>{version.isManualArchive ? 'Ручний архів' : 'Автоархів'}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="recipe-editor-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={handlePrintModal}
                    style={{ ...buttonStyle('accent'), padding: '10px 18px', cursor: 'pointer', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    🖨 Друкувати калькуляцію
                  </button>
                  <button type="button" onClick={handlePrintAccountingModal}
                    style={{ ...buttonStyle('success'), padding: '10px 18px', cursor: 'pointer', fontWeight: 500 }}>
                    Бухгалтерський друк
                  </button>
                </div>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button type="button" onClick={closeModal} style={{ ...buttonStyle('muted'), padding: '10px 20px', cursor: 'pointer' }}>Скасувати</button>
                  <button type="button" onClick={handleSave} disabled={saving}
                    style={{ ...buttonStyle(saving ? 'muted' : 'danger'), padding: '10px 24px', cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 600 }}>
                    {saving ? 'Збереження...' : (editingId ? 'Зберегти' : 'Створити')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {b52ModalOpen && (
        <B52Modal
          title="Страви"
          entityType="dishes"
          onClose={() => setB52ModalOpen(false)}
          onSaved={() => dispatch(fetchDishes())}
        />
      )}
    </div>
  );
};

export default DishesPage;
